import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import * as storageService from '../../services/storageService';
import { Hospital, Patient, STATUS_CONFIG, SUB_STATUS_CONFIG, StaffUser, StaffRole, STAFF_ROLES, ScreenSession, DocAccessToken, HospitalGroup } from '../../types';
import { Plus, Users, LayoutGrid, LogOut, Settings, Activity, History, Edit, Trash2, Key, Phone, QrCode, Download, ChevronDown, ChevronRight, ChevronLeft, Sun, Moon, ShieldCheck, Power, X, Eye, EyeOff, AlertCircle, Monitor, Tv, Tablet, Smartphone, Laptop, FileCode, Copy, CheckCircle2, RefreshCw, BarChart2, Building2, Link, LinkIcon, Clock, Star, Sunrise, Megaphone, DollarSign, Bug } from 'lucide-react';
import HospitalModal from '../../components/HospitalModal';
import GroupModal from '../../components/GroupModal';
import AccessModal from '../../components/AccessModal';
import GroupAccessModal from '../../components/GroupAccessModal';
import GroupLinkModal from '../../components/GroupLinkModal';
import { exportBeautifulPDF } from '../../utils/exportBeautifulPDF';
import CloudflareMetrics from '../../components/CloudflareMetrics';

import { AdminNavbarUser } from '../../components/AdminNavbarUser';
import ExecutiveBrief from '../../components/ExecutiveBrief';
import BillingDashboard from '../../components/BillingDashboard';
import ExecutiveMarketing from '../../components/ExecutiveMarketing';
import StaffSessionsPanel from '../../components/StaffSessionsPanel';
import BugReportsPanel from '../../components/BugReportsPanel';
import GlobalSettings from './GlobalSettings';

const AdminDashboard: React.FC = () => {
    const [hospitals, setHospitals] = useState<Hospital[]>([]);
    const [patients, setPatients] = useState<Patient[]>([]);
    const [groups, setGroups] = useState<HospitalGroup[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();

    // Theme: Fixed yin-yang — sidebar always dark navy, content always light
    const theme = 'light' as const;

    const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
        return localStorage.getItem('adminSidebar') !== 'closed';
    });

    useEffect(() => {
        localStorage.setItem('adminSidebar', isSidebarOpen ? 'open' : 'closed');
    }, [isSidebarOpen]);

    const t = useMemo(() => ({
        // Content area — always light
        bg: 'bg-[#F8FAFC]',
        textMain: 'text-slate-600',
        textWhite: 'text-slate-900',
        textMuted: 'text-slate-500',
        cardBg: 'bg-white/70 backdrop-blur-2xl',
        cardBorder: 'border-slate-200/60',
        cardShadow: 'shadow-[inset_0_1px_0_rgba(255,255,255,1),_0_8px_30px_rgba(0,0,0,0.02)]',
        cardHoverBg: 'hover:bg-white',
        cardHoverBorder: 'border-slate-200/60 hover:border-slate-300',
        navbarBg: 'bg-white/90 backdrop-blur-2xl border-b border-slate-200/60',
        // Sidebar — always dark navy
        sidebarBg: 'bg-gradient-to-b from-[#0B1628] via-[#0F1D35] to-[#0B1628]',
        sidebarBorder: 'border-white/[0.06]',
        sidebarText: 'text-slate-400',
        sidebarTextHover: 'text-slate-200',
        sidebarActive: 'bg-white/[0.08] text-white border-l-[3px] border-indigo-400',
        sidebarHover: 'hover:bg-white/[0.04]',
        sidebarDivider: 'border-white/[0.06]',
        inputBg: 'bg-slate-100/50',
        tableHeader: 'bg-slate-50/80 border-slate-200/60',
        tableRowHover: 'hover:bg-slate-50/50',
        tableRowActive: 'bg-indigo-50/50',
        divider: 'bg-slate-200/60',
        dividerBorder: 'divide-slate-200/60',
        borderBottom: 'border-b border-slate-200/60',
        borderTop: 'border-t border-slate-200/60',
    }), []);


    // Modal State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isAccessModalOpen, setIsAccessModalOpen] = useState(false);
    const [selectedHospital, setSelectedHospital] = useState<Hospital | null>(null);

    const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState<HospitalGroup | null>(null);

    const [isGroupAccessOpen, setIsGroupAccessOpen] = useState(false);
    const [isGroupLinkOpen, setIsGroupLinkOpen] = useState(false);

    // Filters
    const [activeTab, setActiveTabRaw] = useState<'brief' | 'hospitals' | 'chains' | 'history' | 'marketing' | 'billing' | 'screens' | 'staff_sessions' | 'bugs' | 'docs' | 'metrics' | 'settings'>(() => {
        const saved = localStorage.getItem('hs_superadmin_tab') as any;
        // Migrate legacy tab IDs to unified marketing tab
        if (saved === 'satisfaction' || saved === 'ads') return 'marketing';
        if (saved === 'bi') return 'brief'; // Analytics absorbed into Resumen
        return saved || 'brief';
    });
    const setActiveTab = (t: any) => {
        localStorage.setItem('hs_superadmin_tab', t);
        setActiveTabRaw(t);
    };
    const [filterHospitalId, setFilterHospitalId] = useState<string | null>(null);
    const [dateRange, setDateRange] = useState({ start: '', end: '' });
    const [expandedPatientId, setExpandedPatientId] = useState<string | null>(null);
    const [selectedPatientIds, setSelectedPatientIds] = useState<string[]>([]);

    // Screen Fleet Monitoring
    const [screens, setScreens] = useState<ScreenSession[]>([]);
    const [screenFilter, setScreenFilter] = useState<'all' | 'online' | 'offline'>('all');
    const [screenHospitalFilter, setScreenHospitalFilter] = useState<string | null>(null);
    const [editingScreen, setEditingScreen] = useState<ScreenSession | null>(null);
    const [editScreenName, setEditScreenName] = useState('');
    const [editScreenGroup, setEditScreenGroup] = useState('');
    const [editForcedTheme, setEditForcedTheme] = useState<string | null>(null);
    const [editScreenMode, setEditScreenMode] = useState<string>('BOARD');
    const [editMediaUrl, setEditMediaUrl] = useState('');
    const [editBoardLayout, setEditBoardLayout] = useState<string>('LIST');
    const [previewScreen, setPreviewScreen] = useState<ScreenSession | null>(null);

    // Doc Tokens
    const [docTokens, setDocTokens] = useState<DocAccessToken[]>([]);
    const [showNewTokenModal, setShowNewTokenModal] = useState(false);
    const [newTokenLabel, setNewTokenLabel] = useState('');
    const [newTokenHospitalId, setNewTokenHospitalId] = useState('');
    const [creatingToken, setCreatingToken] = useState(false);
    const [copiedTokenId, setCopiedTokenId] = useState<string | null>(null);

    const handleCopyTokenLink = (tokenId: string) => {
        const url = `${window.location.origin}/docs/${tokenId}`;
        navigator.clipboard.writeText(url);
        setCopiedTokenId(tokenId);
        setTimeout(() => setCopiedTokenId(null), 2000);
    };

    const handleCreateToken = async () => {
        if (!newTokenLabel.trim()) return;
        setCreatingToken(true);
        const res = await storageService.createDocToken(newTokenLabel, newTokenHospitalId || undefined);
        if (res.success) {
            setShowNewTokenModal(false);
            setNewTokenLabel('');
            setNewTokenHospitalId('');
            const tokensData = await storageService.getDocTokens();
            setDocTokens(tokensData);
        } else {
            alert('Error al crear token');
        }
        setCreatingToken(false);
    };

    const handleToggleTokenRevoke = async (token: DocAccessToken) => {
        if (token.isRevoked) {
            await storageService.restoreDocToken(token.id);
        } else {
             if (!window.confirm('¿Revocar acceso? El link dejará de funcionar inmediatamente.')) return;
             await storageService.revokeDocToken(token.id);
        }
        const tokensData = await storageService.getDocTokens();
        setDocTokens(tokensData);
    };

    const filteredPatients = useMemo(() => {
        return patients.filter(p => {
            if (filterHospitalId && p.hospitalId !== filterHospitalId) return false;

            if (dateRange.start) {
                const start = new Date(dateRange.start);
                start.setHours(0, 0, 0, 0);
                if (new Date(p.admissionTime) < start) return false;
            }
            if (dateRange.end) {
                const end = new Date(dateRange.end);
                end.setHours(23, 59, 59, 999);
                if (new Date(p.admissionTime) > end) return false;
            }
            return true;
        }).sort((a, b) => new Date(b.admissionTime).getTime() - new Date(a.admissionTime).getTime());
    }, [patients, filterHospitalId, dateRange]);

    const allMovements = useMemo(() => {
        const movements: any[] = [];
        patients.forEach(p => {
            // Apply same filters as patients for export consistency
            if (filterHospitalId && p.hospitalId !== filterHospitalId) return;
            if (dateRange.start) {
                const start = new Date(dateRange.start);
                start.setHours(0, 0, 0, 0);
                if (new Date(p.admissionTime) < start) return;
            }
            if (dateRange.end) {
                const end = new Date(dateRange.end);
                end.setHours(23, 59, 59, 999);
                if (new Date(p.admissionTime) > end) return;
            }

            p.logs.forEach(log => {
                movements.push({
                    patientId: p.id,
                    patientName: p.name || 'Anónimo',
                    procedureName: p.procedureName || 'Sin procedimiento',
                    doctorName: p.doctorName || '',
                    hospitalId: p.hospitalId,
                    status: log.status,
                    subStatus: log.subStatus,
                    condition: log.condition,
                    timestamp: log.timestamp,
                    updatedBy: log.updatedBy
                });
            });
        });
        return movements.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    }, [patients, filterHospitalId, dateRange]);

    const handleSelectAll = () => {
        if (selectedPatientIds.length === filteredPatients.length && filteredPatients.length > 0) {
            setSelectedPatientIds([]);
        } else {
            setSelectedPatientIds(filteredPatients.map(p => p.id));
        }
    };

    const togglePatientSelection = (id: string) => {
        setSelectedPatientIds(prev => prev.includes(id) ? prev.filter(pid => pid !== id) : [...prev, id]);
    };

    const handleDeletePatient = async (patientId: string) => {
        if (!window.confirm("¿Estás seguro de que deseas eliminar permanentemente a este paciente? Esta acción no se puede deshacer.")) return;
        
        setIsLoading(true);
        try {
            await storageService.deletePatientGlobal(patientId);
            await loadData();
            setSelectedPatientIds(prev => prev.filter(id => id !== patientId));
            setExpandedPatientId(null);
        } catch (e) {
            console.error("Error al eliminar paciente:", e);
            alert("No se pudo eliminar el paciente.");
            setIsLoading(false);
        }
    };

    const handleBatchDelete = async () => {
        const count = selectedPatientIds.length;
        if (count === 0) return;
        if (!window.confirm(`¿Estás seguro de que deseas eliminar permanentemente los ${count} pacientes seleccionados? ESTA ACCIÓN ES IRREVERSIBLE.`)) return;

        setIsLoading(true);
        try {
            // Delete sequentially to avoid rate limits or overwhelming the worker
            for (const id of selectedPatientIds) {
                await storageService.deletePatientGlobal(id);
            }
            await loadData();
            setSelectedPatientIds([]);
            setExpandedPatientId(null);
        } catch (e) {
            console.error("Error al eliminar pacientes:", e);
            alert("Hubo un error al eliminar algunos pacientes.");
            await loadData();
        }
    };

    const impersonateHospital = (hospital: Hospital) => {
        window.open(`/${hospital.handle}/staff?impersonate=true`, '_blank');
    };

    const exportToCSV = () => {
        const targetPatients = selectedPatientIds.length > 0
            ? filteredPatients.filter(p => selectedPatientIds.includes(p.id))
            : filteredPatients;

        if (targetPatients.length === 0) return;

        const headers = ['ID', 'Paciente', 'Procedimiento', 'Doctor', 'Hospital', 'Ingreso', 'Hora Inicio Quirófano', 'Hora Fin Quirófano', 'Duración Cirugía (Minutos)', 'Alta'];
        
        const rows = targetPatients.map(p => {
            const hospitalName = hospitals.find(h => h.id === p.hospitalId)?.name || p.hospitalId;
            const admissionStr = p.admissionTime ? new Date(p.admissionTime).toLocaleString('es-MX', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '---';
            const exitStr = p.dischargeTime ? new Date(p.dischargeTime).toLocaleString('es-MX', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : (p.currentStatus === 'EXIT' ? 'Listo para salida' : '---');

            let surgeryStartStr = '---';
            let surgeryEndStr = '---';
            let surgeryDurationStr = '---';

            const sortedLogs = [...(p.logs || [])].sort((a,b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
            
            const surgeryStartLog = sortedLogs.find(l => l.status === 'SURGERY' && (l.subStatus === 'INCISION' || !l.subStatus));
            const surgeryEndLog = sortedLogs.find(l => l.status === 'SURGERY' && l.subStatus === 'FINISHED') 
                || sortedLogs.find(l => l.status === 'RECOVERY')
                || sortedLogs.find(l => l.status === 'EXIT');

            if (surgeryStartLog) {
                try { surgeryStartStr = new Date(surgeryStartLog.timestamp).toLocaleString('es-MX', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }); } catch(e){}
            }
            if (surgeryEndLog) {
                try { surgeryEndStr = new Date(surgeryEndLog.timestamp).toLocaleString('es-MX', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }); } catch(e){}
            }

            if (surgeryStartLog && surgeryEndLog) {
                const ms = new Date(surgeryEndLog.timestamp).getTime() - new Date(surgeryStartLog.timestamp).getTime();
                surgeryDurationStr = Math.max(0, Math.round(ms / 60000)).toString();
            }

            return [
                `"${p.id}"`,
                `"${(p.name || 'Anónimo').replace(/,/g, ' ')}"`,
                `"${(p.procedureName || 'Sin procedimiento').replace(/,/g, ' ')}"`,
                `"${(p.doctorName || '').replace(/,/g, ' ')}"`,
                `"${hospitalName}"`,
                `"${admissionStr}"`,
                `"${surgeryStartStr}"`,
                `"${surgeryEndStr}"`,
                `"${surgeryDurationStr}"`,
                `"${exitStr}"`
            ].join(',');
        });

        const csvContent = "data:text/csv;charset=utf-8,\uFEFF" + [headers.join(','), ...rows].join('\n');
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        const fileName = `reporte_pacientes${filterHospitalId ? `_${filterHospitalId}` : ''}_${new Date().toISOString().split('T')[0]}.csv`;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const exportSinglePatient = (patient: any) => {
        const now = new Date();
        const generatedAt = now.toLocaleString('es-MX', { dateStyle: 'long', timeStyle: 'short' });

        // Basic Info Section
        const basicInfoHeaders = ['ID', 'Nombre', 'Procedimiento', 'Doctor', 'Ingreso', 'Alta'].join(',');
        const cleanName = typeof patient.name === 'string' ? patient.name.replace(/,/g, ' ') : '';
        const cleanProc = typeof patient.procedureName === 'string' ? patient.procedureName.replace(/,/g, ' ') : '';
        const cleanDoctor = typeof patient.doctorName === 'string' ? patient.doctorName.replace(/,/g, ' ') : '';
        
        let admissionTimeStr = '---';
        if (patient.admissionTime) {
          try { admissionTimeStr = new Date(patient.admissionTime).toLocaleString('es-MX', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }); } catch(e){}
        }
        let dischargeTimeStr = '---';
        if (patient.dischargeTime) {
          try { dischargeTimeStr = new Date(patient.dischargeTime).toLocaleString('es-MX', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }); } catch(e){}
        }
        
        const basicInfoRow = `"${patient.id}","${cleanName}","${cleanProc}","${cleanDoctor}","${admissionTimeStr}","${dischargeTimeStr}"`;

        // Logs Section
        const logHeaders = ['Fecha y Hora', 'Estado', 'Sub-Estado', 'Condición'].join(',');
        const logRows = (patient.logs || []).map((log: any) => {
          let timeStr = '---';
          if (log.timestamp) {
            try { timeStr = new Date(log.timestamp).toLocaleString('es-MX', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }); } catch(e){}
          }
          const stLabel = STATUS_CONFIG[log.status as keyof typeof STATUS_CONFIG]?.label || log.status;
          const subLabel = SUB_STATUS_CONFIG[log.subStatus as keyof typeof SUB_STATUS_CONFIG]?.label || log.subStatus;
          const condLabel = typeof log.condition === 'string' ? log.condition.replace(/_/g, ' ') : '';
          return `"${timeStr}","${stLabel}","${subLabel}","${condLabel}"`;
        }).join('\n');

        // Calculate Station Breakdown for CSV
        const sortedLogs = [...(patient.logs || [])].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
        const stations = ['WAITING', 'PREPARATION', 'ROOM_ENTRY', 'ANESTHESIA', 'SURGERY', 'RECOVERY'];
        const calcBreakdown: any[] = [];
        
        for (let i = 0; i < sortedLogs.length; i++) {
            const log = sortedLogs[i];
            if (!stations.includes(log.status)) continue;
            if (calcBreakdown.some(b => b.station === log.status)) continue;
            
            const startTime = new Date(log.timestamp).getTime();
            const nextDiffLog = sortedLogs.slice(i + 1).find(l => l.status !== log.status && stations.includes(l.status));
            
            let endTime;
            if (nextDiffLog) {
                endTime = new Date(nextDiffLog.timestamp).getTime();
            } else {
                const exitLog = sortedLogs.find(l => l.status === 'EXIT');
                if (exitLog) {
                    endTime = new Date(exitLog.timestamp).getTime();
                } else if (patient.dischargeTime) {
                    endTime = new Date(patient.dischargeTime).getTime();
                } else {
                    endTime = new Date().getTime();
                }
            }
            const durationMin = Math.round((endTime - startTime) / 60000);
            calcBreakdown.push({ station: log.status, durationMin });
        }
        
        stations.forEach(st => {
            if (!calcBreakdown.some(b => b.station === st)) calcBreakdown.push({ station: st, durationMin: null });
        });
        calcBreakdown.sort((a,b) => stations.indexOf(a.station) - stations.indexOf(b.station));

        const STATION_LABELS: Record<string, string> = {
            WAITING: 'En Espera', PREPARATION: 'Preparación', ROOM_ENTRY: 'Ingreso a Qx',
            ANESTHESIA: 'Anestesia', SURGERY: 'Cirugía', RECOVERY: 'Recuperación',
        };
        
        const breakdownHeaders = ['Estación', 'Duración (min)'].join(',');
        const breakdownRows = calcBreakdown
            .filter(s => s.durationMin !== null)
            .map(s => `"${STATION_LABELS[s.station] || s.station}","${s.durationMin}"`)
            .join('\n');

        const csvContent = `"HOSPITAL STATUS - REPORTE DE PACIENTE"\n"Generado el: ${generatedAt}"\n\n` +
          `"[Datos del Paciente]"\n${basicInfoHeaders}\n${basicInfoRow}\n\n` +
          `"[Tiempos por Estación]"\n${breakdownHeaders}\n${breakdownRows}\n\n` +
          `"[Historial de Movimientos]"\n${logHeaders}\n${logRows}\n\n` +
          `"Documento generado por el sistema automatizado Hospital Status."`;

        const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const filename = `Reporte_${cleanName || patient.id.slice(0,8)}_${new Date().toISOString().split('T')[0]}.csv`;

        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';

        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };


    useEffect(() => {
        // BúnkerAuth: Cloudflare Access protects this route at the edge.
        // When CF redirects back here, we must exchange the CF JWT for our app session.
        const init = async () => {
            // Proactive Hardening: Invalidate legacy PIN-based SUPER_ADMIN sessions
            const authDataStr = localStorage.getItem('hospitalstatus_auth');
            if (authDataStr) {
                try {
                    const authData = JSON.parse(authDataStr);
                    if (authData.role === 'SUPER_ADMIN' || authData.role === 'GROUP_ADMIN') {
                        console.warn('[Búnker] Legacy PIN session detected for elevated role. Evicting for Zero Trust enforcement.');
                        localStorage.removeItem('hospitalstatus_auth');
                    }
                } catch (e) {}
            }

            // Check if we already have an app session from a previous sync
            const existingCtx = localStorage.getItem('hospitalstatus_admin_ctx');
            
            // Proactive Eviction: If a Hospital Admin somehow has a global context, kick them to /staff
            if (existingCtx) {
                try {
                    const ctx = JSON.parse(existingCtx);
                    if (ctx.role === 'HOSPITAL_ADMIN') {
                        console.warn('[AdminDashboard] Evicting HOSPITAL_ADMIN from global dashboard to /staff');
                        const authData = {
                            token: 'cf_session',
                            hospitalName: 'Hospital',
                            hospitalId: ctx.hospitalId,
                            role: ctx.role,
                            staffId: ctx.id,
                            staffName: ctx.name,
                            staffRole: ctx.role
                        };
                        localStorage.setItem('hospitalstatus_auth', JSON.stringify(authData));
                        window.location.href = '/staff';
                        return;
                    }
                } catch(e) {}
            }

            if (!existingCtx) {
                // No valid Cloudflare-synced session found. 
                // We MUST call /cloudflare-sync to exchange CF JWT cookies for an app session.
                try {
                    console.log('[AdminDashboard] No app session found. Attempting Cloudflare Sync...');
                    const res = await fetch('/api/auth/cloudflare-sync', {
                        method: 'GET',
                        credentials: 'include',
                    });
                    if (res.ok) {
                        const data = await res.json();
                        if (data.success && data.user) {
                            if (data.user.role === 'HOSPITAL_ADMIN') {
                                // Kick Hospital Admin immediately to their local station
                                console.warn('[AdminDashboard] Cloudflare Sync returned HOSPITAL_ADMIN. Routing to /staff');
                                const authData = {
                                    token: 'cf_session',
                                    hospitalName: 'Hospital',
                                    hospitalId: data.user.hospitalId,
                                    role: data.user.role, expiresAt: Date.now() + 48 * 60 * 60 * 1000,
                                    staffId: data.user.id,
                                    staffName: data.user.name,
                                    staffRole: data.user.role
                                };
                                localStorage.setItem('hospitalstatus_auth', JSON.stringify(authData));
                                window.location.href = '/staff';
                                return;
                            } else {
                                localStorage.setItem('hospitalstatus_admin_ctx', JSON.stringify({
                                    id: data.user.id,
                                    name: data.user.name,
                                    role: data.user.role, expiresAt: Date.now() + 48 * 60 * 60 * 1000,
                                    hospitalId: data.user.hospitalId,
                                    groupId: data.user.groupId,
                                }));
                                window.dispatchEvent(new Event('auth-updated'));
                                console.log('[AdminDashboard] CF Sync OK. Session established for:', data.user.name);
                            }
                        }
                    } else {
                        // CF Cookie isn't valid or user is not authorized -> go to admin login
                        console.warn('[AdminDashboard] CF Sync failed. Redirecting to /admin/login');
                        window.location.href = '/admin/login';
                        return;
                    }
                } catch (e) {
                    console.error('[AdminDashboard] CF Sync error:', e);
                    window.location.href = '/admin/login';
                    return;
                }
            }
            loadData();
        };
        init();
    }, []);

    const loadData = async () => {
        setIsLoading(true);
        try {
            const [hospitalsData, patientsData, tokensData, groupsData] = await Promise.all([
                storageService.getHospitals(),
                storageService.getAllPatientsHistory(),
                storageService.getDocTokens(),
                storageService.getHospitalGroups()
            ]);
            setHospitals(hospitalsData);
            setPatients(patientsData);
            setDocTokens(tokensData);
            setGroups(groupsData);

            // Load screens separately (non-blocking — table might not exist yet)
            try {
                const screensData = await storageService.getScreenSessions();
                setScreens(screensData as ScreenSession[]);
            } catch {
                setScreens([]);
            }
        } catch (error: any) {
            console.error("Failed to load dashboard data", error);
            if (error?.message === 'UNAUTHORIZED') {
                return;
            }
        } finally {
            setIsLoading(false);
        }
    };


    const handleCreateHospital = () => {
        setSelectedHospital(null);
        setIsModalOpen(true);
    };

    const handleEditHospital = (hospital: Hospital) => {
        setSelectedHospital(hospital);
        setIsModalOpen(true);
    };

    const handleSaveHospital = () => {
        loadData(); // Refresh list
    };

    const handleDeleteHospital = async (hospitalId: string) => {
        if (window.confirm('¿Estás seguro de que deseas eliminar este hospital? Esta acción no se puede deshacer y borrará todos los datos asociados.')) {
            await storageService.deleteHospital(hospitalId);
            loadData();
        }
    };

    const handleCreateGroup = () => {
        setSelectedGroup(null);
        setIsGroupModalOpen(true);
    };

    const handleEditGroup = (group: HospitalGroup) => {
        setSelectedGroup(group);
        setIsGroupModalOpen(true);
    };

    const handleSaveGroup = () => {
        loadData();
    };

    const handleDeleteGroup = async (groupId: string) => {
        if (window.confirm('¿Eliminar esta cadena? Los hospitales volverán a ser independientes.')) {
            await storageService.deleteHospitalGroup(groupId);
            loadData();
        }
    };

    const impersonateGroup = (group: HospitalGroup) => {
        window.open(`/c/${group.handle}/dashboard?impersonate=true`, '_blank');
    };

    const handleLogout = async () => {
        await storageService.logout();
        
        // Revocar la sesión de Cloudflare Access para forzar re-autenticación
        // Si estamos en un subdominio de workers.dev (como en staging default), omitimos Access.
        if (window.location.hostname.includes('workers.dev')) {
            window.location.href = '/admin/';
        } else {
            window.location.href = `${window.location.origin}/cdn-cgi/access/logout?returnTo=${encodeURIComponent(window.location.origin + '/admin/login')}`;
        }
    };

    // Derived Stats
    const totalActiveHospitals = hospitals.filter(h => h.isActive !== false).length;
    const totalActiveRooms = hospitals.reduce((acc, h) => acc + (h.isActive !== false ? h.maxRooms : 0), 0);
    const totalPatientsMonth = patients.length;

    const renderHospitalCard = (hospital: Hospital) => (
        <div key={hospital.id} className={`${t.cardBg} rounded-[2rem] p-0 border shadow-sm ${t.cardShadow} hover:shadow-xl transition-all duration-300 group overflow-hidden ${hospital.isActive === false ? 'opacity-60 grayscale border-white/5' : `${t.cardBorder} hover:border-indigo-500/20`}`}>
            
            <div className={`p-6 sm:p-8 flex flex-col h-full`}>
                <div className={`flex justify-between items-start mb-6`}>
                    <div className={`flex items-center gap-5`}>
                        <div className={`w-14 h-14 rounded-2xl ${t.cardBg} border ${t.cardBorder} flex items-center justify-center p-2.5 relative overflow-hidden shadow-sm`}>
                            {theme === 'dark' && hospital.branding.logoDarkUrl ? (
                                <img src={hospital.branding.logoDarkUrl} className={`w-full h-full object-contain relative z-10`} alt="Logo" />
                            ) : hospital.branding.logoUrl ? (
                                <img src={hospital.branding.logoUrl} className={`w-full h-full object-contain relative z-10`} alt="Logo" />
                            ) : (
                                <BuildingIcon className={`w-6 h-6 ${t.textMuted} relative z-10`} />
                            )}
                        </div>
                        <div>
                            <h3 className={`font-black text-xl ${t.textWhite} tracking-tight mb-1 group-hover:text-indigo-500 transition-colors`}>{hospital.name}</h3>
                            <a
                                href={`/${hospital.handle}`}
                                target="_blank"
                                rel="noreferrer"
                                className={`text-[10px] font-bold text-slate-500 hover:text-indigo-500 transition-colors uppercase tracking-widest flex items-center gap-1`}
                            >
                                <LinkIcon className="w-3 h-3" /> /{hospital.handle}
                            </a>
                        </div>
                    </div>
                </div>

                <div className={`grid grid-cols-3 gap-2 mb-8`}>
                    <div className={`flex flex-col`}>
                        <span className={`text-[10px] font-bold ${t.textMuted} uppercase tracking-widest mb-1.5`}>Quirófanos</span>
                        <span className={`text-2xl font-black ${t.textWhite}`}>{hospital.maxRooms}</span>
                    </div>
                    <button
                        onClick={() => {
                            setFilterHospitalId(hospital.id);
                            setActiveTab('history');
                        }}
                        className={`flex flex-col text-left group/btn`}
                    >
                        <span className={`text-[10px] font-bold ${t.textMuted} uppercase tracking-widest mb-1.5 group-hover/btn:text-indigo-400 transition-colors`}>Pacientes</span>
                        <span className={`text-2xl font-black ${t.textWhite}`}>{hospital.totalPatients}</span>
                    </button>
                    <div className={`flex flex-col`}>
                        <span className={`text-[10px] font-bold ${t.textMuted} uppercase tracking-widest mb-1.5`}>Personal</span>
                        <div className={`text-2xl font-black ${t.textWhite} flex items-center gap-2`}>
                            {hospital.totalStaff !== undefined ? hospital.totalStaff : '-'}
                            {(hospital.totalStaff === 0 || hospital.totalStaff === undefined) && (
                                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" title="Sin staff" />
                            )}
                        </div>
                    </div>
                </div>

                <div className={`flex items-center gap-2 mt-auto pt-4 ${t.borderTop}`}>
                    <button
                        onClick={() => impersonateHospital(hospital)}
                        title="Dash & APIs"
                        className={`flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-500/10 text-indigo-500 hover:bg-indigo-500 hover:text-white rounded-xl transition-all shadow-sm active:scale-95 text-[11px] font-bold uppercase tracking-wide`}
                    >
                        <Eye className={`w-3.5 h-3.5`} /> <span className="hidden xl:inline">Dash</span>
                    </button>
                    <button
                        onClick={() => { setSelectedHospital(hospital); setIsAccessModalOpen(true); }}
                        className={`flex-1 flex items-center justify-center gap-2 py-2.5 bg-slate-500/5 hover:bg-slate-500/10 ${t.textMain} border border-transparent hover:border-slate-500/20 rounded-xl text-[11px] font-bold uppercase tracking-wide transition-all active:scale-95`}
                    >
                        <Key className={`w-3.5 h-3.5`} /> Accesos
                    </button>
                    <button
                        onClick={() => handleEditHospital(hospital)}
                        className={`flex-1 flex items-center justify-center gap-2 py-2.5 bg-slate-500/5 hover:bg-slate-500/10 ${t.textMain} border border-transparent hover:border-slate-500/20 rounded-xl text-[11px] font-bold uppercase tracking-wide transition-all active:scale-95`}
                    >
                        <Settings className={`w-3.5 h-3.5`} /> Editar
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <div className={`min-h-screen ${t.bg} font-sans relative overflow-hidden flex flex-col xl:flex-row ${t.textMain}`}>
            {/* Ambient Background Orbs */}
            <div className="fixed top-0 right-0 w-1/2 h-96 bg-indigo-500/5 blur-[120px] rounded-full pointer-events-none z-0"></div>
            <div className="fixed bottom-0 left-0 w-1/2 h-96 bg-emerald-500/5 blur-[120px] rounded-full pointer-events-none z-0"></div>

            {/* Desktop Left Sidebar */}
            <aside className={`hidden xl:flex flex-col ${isSidebarOpen ? 'w-[260px]' : 'w-[72px]'} fixed inset-y-0 left-0 z-40 ${t.sidebarBg} border-r ${t.sidebarBorder} transition-all duration-300 ease-in-out`}>
                {/* Logo */}
                <div className={`px-4 pt-6 pb-5 shrink-0 flex items-center ${isSidebarOpen ? 'gap-3' : 'justify-center'} transition-all border-b ${t.sidebarDivider}`}>
                    <img 
                        src="/logo-final-azul.png" 
                        alt="Hospital Status" 
                        className={`${isSidebarOpen ? 'w-10 h-10' : 'w-9 h-9'} rounded-xl object-contain flex-shrink-0 transition-all`}
                    />
                    {isSidebarOpen && (
                        <h1 className="text-[15px] font-black text-white tracking-tight font-heading truncate">
                            Hospital Status
                        </h1>
                    )}
                </div>

                {/* Navigation Items */}
                <div className={`flex-1 overflow-y-auto px-3 pt-4 space-y-0.5 scrollbar-hide`}>
                    {/* Morning Brief */}
                    {(() => {
                        const briefItem = { id: 'brief', label: 'Resumen', icon: Sunrise };
                        const isActive = activeTab === briefItem.id;
                        return (
                            <button
                                key={briefItem.id}
                                onClick={() => setActiveTab(briefItem.id as any)}
                                title={!isSidebarOpen ? briefItem.label : undefined}
                                className={`w-full ${isSidebarOpen ? 'px-3 py-2.5 text-left gap-3' : 'py-2.5 justify-center text-center'} rounded-lg text-[13px] font-semibold transition-all duration-200 flex items-center mb-1 ${isActive ? t.sidebarActive : `${t.sidebarText} ${t.sidebarHover} border-l-[3px] border-transparent`}`}
                            >
                                <briefItem.icon className={`w-[18px] h-[18px] flex-shrink-0 ${isActive ? '' : ''}`} />
                                {isSidebarOpen && <span className="whitespace-nowrap">{briefItem.label}</span>}
                            </button>
                        );
                    })()}

                    {/* Section: Operaciones */}
                    {isSidebarOpen && <div className={`pt-4 pb-1.5 px-3`}><p className={`text-[9px] font-black uppercase tracking-[0.2em] text-slate-500`}>Operaciones</p></div>}
                    {!isSidebarOpen && <div className={`w-6 h-px bg-white/[0.06] mx-auto my-3`} />}
                    {[
                        { id: 'hospitals', label: 'Hospitales', icon: LayoutGrid },
                        { id: 'chains', label: 'Cadenas', icon: Building2 },
                        { id: 'history', label: 'Historial', icon: Clock },
                        { id: 'screens', label: 'Pantallas', icon: Monitor },
                        { id: 'staff_sessions', label: 'Staff', icon: Users },
                        { id: 'bugs', label: 'Reportes', icon: Bug },
                    ].map(item => {
                        const isActive = activeTab === item.id;
                        return (
                            <button
                                key={item.id}
                                onClick={() => setActiveTab(item.id as any)}
                                title={!isSidebarOpen ? item.label : undefined}
                                className={`w-full ${isSidebarOpen ? 'px-3 py-2 text-left gap-3' : 'py-2 justify-center text-center'} rounded-lg text-[13px] font-semibold transition-all duration-200 flex items-center ${isActive ? t.sidebarActive : `${t.sidebarText} ${t.sidebarHover} border-l-[3px] border-transparent`}`}
                            >
                                <item.icon className={`w-[18px] h-[18px] flex-shrink-0`} />
                                {isSidebarOpen && <span className="whitespace-nowrap flex-1">{item.label}</span>}
                                {isSidebarOpen && (item as any).badge && (
                                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center ${
                                        (item as any).badgeColor === 'red'
                                            ? (isActive ? 'bg-red-500/20 text-red-300' : 'bg-red-500/10 text-red-400')
                                            : (isActive ? 'bg-white/15 text-white' : `${theme === 'dark' ? 'bg-white/[0.08] text-slate-400' : 'bg-indigo-100 text-indigo-600'}`)
                                    }`}>{(item as any).badge}</span>
                                )}
                            </button>
                        );
                    })}

                    {/* Section: Negocio */}
                    {isSidebarOpen && <div className={`pt-5 pb-1.5 px-3`}><p className={`text-[9px] font-black uppercase tracking-[0.2em] text-slate-500`}>Negocio</p></div>}
                    {!isSidebarOpen && <div className={`w-6 h-px bg-white/[0.06] mx-auto my-3`} />}
                    {[
                        { id: 'marketing', label: 'Marketing', icon: Megaphone },
                        { id: 'billing', label: 'Finanzas', icon: DollarSign },
                    ].map(item => {
                        const isActive = activeTab === item.id;
                        return (
                            <button
                                key={item.id}
                                onClick={() => setActiveTab(item.id as any)}
                                title={!isSidebarOpen ? item.label : undefined}
                                className={`w-full ${isSidebarOpen ? 'px-3 py-2 text-left gap-3' : 'py-2 justify-center text-center'} rounded-lg text-[13px] font-semibold transition-all duration-200 flex items-center ${isActive ? t.sidebarActive : `${t.sidebarText} ${t.sidebarHover} border-l-[3px] border-transparent`}`}
                            >
                                <item.icon className={`w-[18px] h-[18px] flex-shrink-0`} />
                                {isSidebarOpen && <span className="whitespace-nowrap">{item.label}</span>}
                            </button>
                        );
                    })}

                    {/* Section: Técnico */}
                    {isSidebarOpen && <div className={`pt-5 pb-1.5 px-3`}><p className={`text-[9px] font-black uppercase tracking-[0.2em] text-slate-500`}>Técnico</p></div>}
                    {!isSidebarOpen && <div className={`w-6 h-px bg-white/[0.06] mx-auto my-3`} />}
                    {[
                        { id: 'docs', label: 'API & Tokens', icon: FileCode },
                        { id: 'metrics', label: 'Cloud', icon: Activity },
                        { id: 'settings', label: 'Defaults', icon: Settings },
                    ].map(item => {
                        const isActive = activeTab === item.id;
                        return (
                            <button
                                key={item.id}
                                onClick={() => setActiveTab(item.id as any)}
                                title={!isSidebarOpen ? item.label : undefined}
                                className={`w-full ${isSidebarOpen ? 'px-3 py-2 text-left gap-3' : 'py-2 justify-center text-center'} rounded-lg text-[13px] font-semibold transition-all duration-200 flex items-center ${isActive ? t.sidebarActive : `${t.sidebarText} ${t.sidebarHover} border-l-[3px] border-transparent`}`}
                            >
                                <item.icon className={`w-[18px] h-[18px] flex-shrink-0`} />
                                {isSidebarOpen && <span className="whitespace-nowrap">{item.label}</span>}
                            </button>
                        );
                    })}
                </div>

                {/* User Widget + Footer */}
                <div className={`shrink-0 border-t ${t.sidebarDivider}`}>
                    <div className={`px-3 pt-3 pb-2`}>
                        <AdminNavbarUser theme="dark" variant="sidebar" collapsed={!isSidebarOpen} />
                    </div>
                    <div className={`px-3 pb-3 flex ${isSidebarOpen ? 'items-center justify-between' : 'flex-col gap-2 items-center'}`}>
                        <button 
                            onClick={() => setIsSidebarOpen(!isSidebarOpen)} 
                            title={isSidebarOpen ? 'Colapsar menú' : 'Expandir menú'}
                            className="p-2 rounded-lg transition-all text-slate-500 hover:text-slate-300 hover:bg-white/[0.05]"
                        >
                            {isSidebarOpen ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </button>
                        <button 
                            onClick={handleLogout} 
                            title="Cerrar sesión"
                            className="p-2 rounded-lg transition-all text-slate-500 hover:text-red-400 hover:bg-red-500/10"
                        >
                            <LogOut className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </aside>

            {/* Mobile Top Navbar (Visible only < xl) */}
            <nav className={`xl:hidden w-full ${t.navbarBg} backdrop-blur-xl ${t.borderBottom} sticky top-0 z-40 flex flex-col`}>
                <div className={`w-full px-4 h-16 flex items-center justify-between`}>
                    <div className={`flex items-center gap-3 shrink-0`}>
                        <img src="/logo-final-azul.png" alt="Hospital Status" className="w-9 h-9 rounded-xl object-contain" />
                        <h1 className={`text-xl font-black ${t.textWhite} tracking-tight font-heading`}>
                            Hospital Status
                        </h1>
                    </div>
                    <div className={`flex items-center gap-2 shrink-0`}>
                        <button onClick={handleLogout} className={`p-2 rounded-xl transition-all ${t.textMuted} hover:text-red-400 hover:bg-red-400/10`}>
                            <LogOut className={`w-5 h-5`} />
                        </button>
                    </div>
                </div>

                {/* Mobile / Tablet Tabs */}
                <div className={`w-full px-4 pb-3 overflow-x-auto scrollbar-hide flex gap-2`}>
                    {[
                        { id: 'brief', label: 'Resumen', icon: Sunrise },
                        { id: 'hospitals', label: 'Hospitales', icon: LayoutGrid },
                        { id: 'chains', label: 'Cadenas', icon: Building2 },
                        { id: 'history', label: 'Historial', icon: Clock },

                        { id: 'marketing', label: 'Marketing', icon: Megaphone },
                        { id: 'screens', label: 'Pantallas', icon: Monitor },
                        { id: 'staff_sessions', label: 'Staff', icon: Users },
                        { id: 'docs', label: 'API', icon: FileCode },
                        { id: 'metrics', label: 'Cloud', icon: Activity },
                    ].map(item => (
                        <button
                            key={item.id}
                            onClick={() => setActiveTab(item.id as any)}
                            className={`px-4 py-2 whitespace-nowrap flex-shrink-0 rounded-xl text-sm font-bold transition-all flex items-center gap-2 ${activeTab === item.id ? 'bg-indigo-600 text-white shadow-md' : 'bg-white text-slate-500 hover:text-slate-900 shadow-sm border border-slate-200'}`}
                        >
                            <item.icon className="w-4 h-4" /> {item.label}
                        </button>
                    ))}
                </div>
            </nav>

            {/* Main Content Area */}
            <main className={`flex-1 ${isSidebarOpen ? 'xl:ml-[260px] xl:max-w-[calc(100vw-260px)]' : 'xl:ml-[72px] xl:max-w-[calc(100vw-72px)]'} w-full max-w-[100vw] min-h-screen relative z-10 transition-all duration-300 ease-in-out`}>
                <div className={`max-w-[1600px] mx-auto px-6 md:px-10 lg:px-16 py-8 lg:py-12`}>

                {/* KPI Cards Toggle Header */}


                {/* KPI Cards Data*/}
                {activeTab === 'brief' && (
                  <div className={`grid grid-cols-1 md:grid-cols-4 gap-6 mb-10 animate-fade-in`}>
                      <button
                        onClick={() => setActiveTab('hospitals')}
                        className={`${t.cardBg} p-6 rounded-3xl border ${t.cardBorder} ${t.cardShadow} ${t.cardHoverBorder} transition-all duration-300 flex flex-col justify-between relative overflow-hidden group text-left cursor-pointer hover:-translate-y-1 hover:shadow-xl`}
                      >
                        <div className="flex justify-between items-start mb-4 relative z-10">
                            <div className={`p-2.5 bg-indigo-500/10 text-indigo-400 rounded-xl border border-indigo-500/20 group-hover:scale-110 transition-transform duration-300`}>
                                <BuildingIcon className={`w-5 h-5`} />
                            </div>
                        </div>
                        <div className={`relative z-10 mt-2`}>
                            <p className={`text-4xl font-black ${t.textWhite} tracking-tight`}>{totalActiveHospitals} <span className={`text-lg ${t.textMuted} font-medium`}>/ {hospitals.length}</span></p>
                            <p className={`text-xs font-bold ${t.textMuted} tracking-wide mt-1`}>Hospitales Activos</p>
                        </div>
                    </button>
                    
                    <button
                        onClick={() => {
                            setFilterHospitalId(null);
                            setActiveTab('history');
                        }}
                        className={`${t.cardBg} p-6 rounded-3xl border ${t.cardBorder} ${t.cardShadow} hover:border-emerald-500/30 transition-all duration-300 flex flex-col justify-between relative overflow-hidden group text-left`}
                    >
                        <div className="flex justify-between items-start mb-4 relative z-10 w-full">
                            <div className={`p-2.5 bg-emerald-500/10 text-emerald-400 rounded-xl border border-emerald-500/20 group-hover:scale-110 transition-transform duration-300`}>
                                <Users className={`w-5 h-5`} />
                            </div>
                            <div className={`group/info relative`}>
                                <Activity className={`w-4 h-4 text-emerald-400/50 cursor-help group-hover:text-emerald-400 transition-colors`} />
                                <div className={`absolute bottom-full right-0 mb-2 px-3 py-2 bg-slate-800 text-white text-[10px] rounded-lg opacity-0 group-hover/info:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none shadow-xl border border-white/10`}>
                                    Suma total de pacientes registrados en todas las sucursales
                                </div>
                            </div>
                        </div>
                        <div className={`relative z-10 mt-2`}>
                            <p className={`text-4xl font-black ${t.textWhite} tracking-tight`}>{totalPatientsMonth}</p>
                            <p className={`text-xs font-bold ${t.textMuted} tracking-wide mt-1`}>Pacientes Históricos</p>
                        </div>
                    </button>
                    
                    <button
                        onClick={() => setActiveTab('hospitals')}
                        className={`${t.cardBg} p-6 rounded-3xl border ${t.cardBorder} ${t.cardShadow} ${t.cardHoverBorder} transition-all duration-300 flex flex-col justify-between relative overflow-hidden group text-left cursor-pointer hover:-translate-y-1 hover:shadow-xl`}
                    >
                        <div className="flex justify-between items-start mb-4 relative z-10">
                            <div className={`p-2.5 bg-blue-500/10 text-blue-400 rounded-xl border border-blue-500/20 group-hover:scale-110 transition-transform duration-300`}>
                                <LayoutGrid className={`w-5 h-5`} />
                            </div>
                        </div>
                        <div className={`relative z-10 mt-2`}>
                            <p className={`text-4xl font-black ${t.textWhite} tracking-tight`}>{totalActiveRooms}</p>
                            <p className={`text-xs font-bold ${t.textMuted} tracking-wide mt-1`}>Quirófanos Totales</p>
                        </div>
                    </button>
                    
                    <button
                        onClick={() => setActiveTab('metrics')}
                        className={`${t.cardBg} p-6 rounded-3xl border ${t.cardBorder} ${t.cardShadow} ${t.cardHoverBorder} transition-all duration-300 flex flex-col justify-between relative overflow-hidden group text-left cursor-pointer hover:-translate-y-1 hover:shadow-xl`}
                    >
                        <div className="flex justify-between items-start mb-4 relative z-10">
                            <div className={`p-2.5 bg-amber-500/10 text-amber-400 rounded-xl border border-amber-500/20 group-hover:scale-110 transition-transform duration-300`}>
                                <Activity className={`w-5 h-5`} />
                            </div>
                        </div>
                        <div className={`relative z-10 mt-2`}>
                            <div className="flex items-center gap-2 mb-1">
                                <span className={`w-2 h-2 rounded-full bg-emerald-400 animate-pulse shadow-[0_0_8px_rgba(52,211,153,0.8)]`}></span>
                                <p className={`text-lg font-bold text-emerald-400 tracking-tight leading-none`}>Operativo</p>
                            </div>
                            <p className={`text-xs font-bold ${t.textMuted} tracking-wide mt-2`}>Estado del Sistema</p>
                        </div>
                    </button>
                </div>
                )}

                {isLoading ? (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-pulse">
                       {[1, 2, 3, 4].map(i => (
                           <div key={i} className={`h-48 rounded-3xl ${theme === 'dark' ? 'bg-white/5' : 'bg-slate-200/50'}`} />
                       ))}
                    </div>
                ) : (
                    <>
                        {/* Executive Brief Tab */}
                        {activeTab === 'brief' && (
                            <div className={`animate-fade-in`}>
                                <ExecutiveBrief
                                    theme={theme}
                                    hospitals={hospitals}
                                    patients={patients}
                                    screens={screens}
                                    onNavigate={(tab) => setActiveTab(tab as any)}
                                />
                            </div>
                        )}

                        {activeTab === 'settings' && (
                            <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 ease-out">
                                <GlobalSettings />
                            </div>
                        )}

                        {/* Marketing & Reputación Tab — Vista Ejecutiva */}
                        {activeTab === 'marketing' && (
                            <div className={`animate-fade-in`}>
                                <ExecutiveMarketing
                                    theme={theme}
                                    hospitals={hospitals}
                                />
                            </div>
                        )}

                        {/* Billing Tab */}
                        {activeTab === 'billing' && (
                            <div className={`animate-fade-in`}>
                                <BillingDashboard
                                    theme={theme}
                                    hospitals={hospitals}
                                    groups={groups}
                                    onNavigate={(tab) => setActiveTab(tab as any)}
                                />
                            </div>
                        )}

                        {/* Main Content Area */}
                        {activeTab === 'hospitals' && (
                            <div className={`animate-fade-in`}>
                                <div className={`flex items-center justify-between mb-6`}>
                                    <h2 className={`text-xl font-bold ${t.textWhite}`}>Directorio de Hospitales</h2>
                                    <button
                                        onClick={handleCreateHospital}
                                        className={`bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2.5 px-5 rounded-xl flex items-center gap-2 shadow-lg shadow-indigo-600/20 transition-all active:scale-95`}
                                    >
                                        <Plus className={`w-5 h-5`} />
                                        Nuevo Hospital
                                    </button>
                                </div>

                                <div className={`space-y-12`}>
                                    {/* Groups */}
                                    {groups.filter(g => g.isActive).map(group => {
                                        const groupHospitals = hospitals.filter(h => h.groupId === group.id);
                                        if (groupHospitals.length === 0) return null;
                                        return (
                                            <div key={group.id} className="relative">
                                                <div className={`flex items-center gap-4 mb-6 sticky top-20 z-20 ${t.navbarBg} backdrop-blur-md p-4 rounded-2xl border ${t.cardBorder} shadow-sm`}>
                                                    <div className={`w-12 h-12 rounded-xl bg-white border ${t.cardBorder} flex items-center justify-center p-2 shadow-sm`}>
                                                        {group.logoUrl ? (
                                                            <img src={group.logoUrl} alt={group.name} className="w-full h-full object-contain" />
                                                        ) : (
                                                            <Building2 className={`w-6 h-6 text-indigo-500`} />
                                                        )}
                                                    </div>
                                                    <div>
                                                        <h3 className={`text-xl font-black ${t.textWhite}`}>{group.name}</h3>
                                                        <p className={`text-xs font-bold text-indigo-500`}>Red Corporativa • {groupHospitals.length} sucursales</p>
                                                    </div>
                                                </div>
                                                <div className={`grid grid-cols-1 lg:grid-cols-2 gap-6 pl-4 md:pl-8 border-l-2 ${t.cardBorder}`}>
                                                    {groupHospitals.map(renderHospitalCard)}
                                                </div>
                                            </div>
                                        )
                                    })}

                                    {/* Independent Hospitals */}
                                    {(() => {
                                        const independentHospitals = hospitals.filter(h => !h.groupId || !groups.find(g => g.id === h.groupId));
                                        if (independentHospitals.length === 0) return null;
                                        return (
                                            <div className="relative pt-4">
                                                <div className={`flex items-center gap-3 mb-6`}>
                                                    <div className={`p-2 rounded-xl bg-slate-500/10 text-slate-400 border border-slate-500/20`}>
                                                        <LayoutGrid className={`w-5 h-5`} />
                                                    </div>
                                                    <h3 className={`text-lg font-bold ${t.textWhite}`}>Hospitales Independientes</h3>
                                                </div>
                                                <div className={`grid grid-cols-1 lg:grid-cols-2 gap-6 pl-4 md:pl-8 border-l-2 ${t.borderBottom} border-dashed`}>
                                                    {independentHospitals.map(renderHospitalCard)}
                                                </div>
                                            </div>
                                        )
                                    })()}
                                </div>
                            </div>
                        )}

                        {activeTab === 'chains' && (
                            <div className={`animate-fade-in`}>
                                <div className={`flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6`}>
                                    <div>
                                        <h2 className={`text-2xl font-black ${t.textWhite} mb-1 flex items-center gap-2`}><Building2 className={`w-6 h-6 text-indigo-500`} /> Cadenas Hospitalarias</h2>
                                        <p className={`text-sm ${t.textMuted} font-medium`}>Administra los grupos y corporativos que engloban varios hospitales.</p>
                                    </div>
                                    <button
                                        onClick={handleCreateGroup}
                                        className={`bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 px-6 rounded-2xl flex items-center gap-2 shadow-lg shadow-indigo-600/30 transition-all active:scale-95`}
                                    >
                                        <Plus className={`w-5 h-5`} />
                                        Nueva Cadena
                                    </button>
                                </div>

                                {groups.length === 0 ? (
                                    <div className={`${t.cardBg} backdrop-blur-xl rounded-3xl border border-dashed ${t.cardBorder} p-12 text-center flex flex-col items-center justify-center`}>
                                        <Building2 className={`w-16 h-16 ${t.textMuted} opacity-30 mb-4`} />
                                        <h3 className={`text-xl font-bold ${t.textWhite} mb-2`}>No hay Cadenas Hospitalarias registradas</h3>
                                        <p className={`text-sm ${t.textMuted} max-w-md mx-auto mb-6`}>Crea tu primer grupo para agrupar múltiples hospitales bajo una misma organización de manera visual y estructurada.</p>
                                        <button
                                            onClick={handleCreateGroup}
                                            className={`bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-400 border border-indigo-500/20 px-6 py-2.5 rounded-xl font-bold transition-all flex items-center gap-2`}
                                        >
                                            <Plus className="w-4 h-4" /> Registrar Primera Cadena
                                        </button>
                                    </div>
                                ) : (
                                    <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6`}>
                                        {groups.map(group => {
                                            const groupHospitals = hospitals.filter(h => h.groupId === group.id);
                                            return (
                                                <div key={group.id} className={`${t.cardBg} backdrop-blur-xl rounded-3xl p-6 border ${t.cardBorder} shadow-lg hover:shadow-xl transition-all ${!group.isActive ? 'opacity-60 grayscale' : 'hover:border-indigo-500/30'}`}>
                                                    <div className="flex items-start justify-between mb-4">
                                                        <div className={`w-16 h-16 bg-white rounded-2xl border ${t.cardBorder} flex items-center justify-center p-2 shadow-sm`}>
                                                            {group.logoUrl ? (
                                                                <img src={group.logoUrl} alt={group.name} className="w-full h-full object-contain" />
                                                            ) : (
                                                                <Building2 className="w-8 h-8 text-indigo-500" />
                                                            )}
                                                        </div>
                                                        <div className={`flex items-center gap-2`}>
                                                            {!group.isActive && (
                                                                <span className="text-[10px] bg-slate-500/10 text-slate-400 px-2 py-1 rounded-full font-bold uppercase tracking-wider">Inactiva</span>
                                                            )}
                                                            <div className="flex gap-1">
                                                                <button onClick={() => impersonateGroup(group)} className="p-2 hover:bg-slate-500/10 text-slate-400 rounded-lg transition-colors" title="Entrar como Admin de Cadena">
                                                                    <Eye className="w-4 h-4" />
                                                                </button>
                                                                <button onClick={() => handleEditGroup(group)} className="p-2 hover:bg-indigo-500/10 text-indigo-400 rounded-lg transition-colors" title="Editar Cadena">
                                                                    <Settings className="w-4 h-4" />
                                                                </button>
                                                                {groupHospitals.length === 0 && (
                                                                    <button onClick={() => handleDeleteGroup(group.id)} className="p-2 hover:bg-red-500/10 text-red-500 rounded-lg transition-colors" title="Eliminar Cadena">
                                                                        <Trash2 className="w-4 h-4" />
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    
                                                    <h3 className={`text-xl font-black ${t.textWhite} mb-1`}>{group.name}</h3>
                                                    <div className="flex items-center gap-2 text-xs font-mono text-indigo-400 bg-indigo-500/10 px-2 py-1 rounded-md w-fit mb-4">
                                                        <LinkIcon className="w-3 h-3" /> @{group.handle}
                                                    </div>

                                                    <div className={`pt-4 border-t ${t.cardBorder} flex items-center justify-between`}>
                                                        <div className="flex -space-x-2">
                                                            {groupHospitals.slice(0, 3).map(h => (
                                                                <div key={h.id} className={`w-8 h-8 rounded-full border-2 ${theme === 'dark' ? 'border-slate-900 bg-slate-800' : 'border-white bg-slate-100'} flex items-center justify-center overflow-hidden`}>
                                                                    {h.branding.logoUrl ? (
                                                                        <img src={h.branding.logoUrl} className="w-full h-full object-contain bg-white" />
                                                                    ) : (
                                                                        <Building2 className="w-4 h-4 text-slate-400" />
                                                                    )}
                                                                </div>
                                                            ))}
                                                            {groupHospitals.length > 3 && (
                                                                <div className={`w-8 h-8 rounded-full border-2 ${theme === 'dark' ? 'border-slate-900 bg-slate-800' : 'border-white bg-slate-100'} flex items-center justify-center text-[10px] font-bold ${t.textWhite}`}>
                                                                    +{groupHospitals.length - 3}
                                                                </div>
                                                            )}
                                                            {groupHospitals.length === 0 && (
                                                                <div className="text-xs text-slate-500 font-medium">Sin hospitales asignados</div>
                                                            )}
                                                        </div>
                                                        <div className="text-right">
                                                            <div className={`text-2xl font-black ${t.textWhite} leading-none`}>{groupHospitals.length}</div>
                                                            <div className={`text-[10px] font-bold ${t.textMuted} uppercase tracking-wider`}>Sucursales</div>
                                                        </div>
                                                    </div>

                                                    <div className={`flex gap-2 pt-4 mt-4 ${t.borderTop}`}>
                                                        <button
                                                            onClick={() => { setSelectedGroup(group); setIsGroupAccessOpen(true); }}
                                                            className={`flex-1 flex items-center justify-center gap-2 py-3 ${t.cardBg} ${t.cardHoverBg} border ${t.cardBorder} ${t.textWhite} rounded-xl text-sm font-bold shadow-sm transition-all active:scale-95`}
                                                        >
                                                            <Key className={`w-4 h-4 text-emerald-500`} /> <span className="hidden sm:inline">Accesos</span>
                                                        </button>
                                                        <button
                                                            onClick={() => { setSelectedGroup(group); setIsGroupLinkOpen(true); }}
                                                            className={`flex-1 flex items-center justify-center gap-2 py-3 bg-indigo-600/80 hover:bg-indigo-600 border border-indigo-500/50 text-white rounded-xl text-sm font-bold transition-all shadow-[0_0_15px_rgba(79,70,229,0.3)] hover:shadow-[0_0_25px_rgba(79,70,229,0.5)] active:scale-95`}
                                                        >
                                                            <LinkIcon className={`w-4 h-4`} /> <span className="hidden sm:inline">Vincular</span>
                                                        </button>
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </div>
                                )}
                            </div>
                        )}

                        {activeTab === 'history' && (
                            <div className={`${t.cardBg} backdrop-blur-lg rounded-3xl shadow-xl border ${t.cardBorder} overflow-hidden animate-fade-in`}>
                                <div className={`p-6 ${t.borderBottom}`}>
                                    <div className={`flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6`}>
                                        <div className={`flex items-center gap-3`}>
                                            <div className={`p-2 bg-indigo-500/20 rounded-xl text-indigo-400 border border-indigo-500/20 shadow-[0_0_15px_rgba(99,102,241,0.2)]`}>
                                                <History className={`w-5 h-5`} />
                                            </div>
                                            <h3 className={`font-bold ${t.textWhite} text-xl`}>Historial de Pacientes</h3>
                                        </div>
                                        <button
                                            onClick={exportToCSV}
                                            disabled={filteredPatients.length === 0}
                                            className={`${t.cardBg} ${t.cardHoverBg} border ${t.cardBorder} ${t.textWhite} font-bold py-2.5 px-5 rounded-xl flex items-center justify-center gap-2 shadow-sm transition-all text-sm disabled:opacity-50 disabled:cursor-not-allowed active:scale-95`}
                                        >
                                            <Download className={`w-4 h-4`} />
                                            {selectedPatientIds.length > 0 ? `Exportar Seleccionados (${selectedPatientIds.length})` : 'Exportar Reporte Principal'}
                                        </button>
                                        
                                        {selectedPatientIds.length > 0 && (
                                            <button
                                                onClick={handleBatchDelete}
                                                className={`bg-red-500/10 hover:bg-red-500/20 text-red-500 border border-red-500/20 font-bold py-2.5 px-5 rounded-xl flex items-center justify-center gap-2 shadow-sm transition-all text-sm active:scale-95 ml-2`}
                                                title="Eliminar pacientes seleccionados"
                                            >
                                                <Trash2 className={`w-4 h-4`} />
                                                Eliminar Seleccionados ({selectedPatientIds.length})
                                            </button>
                                        )}
                                    </div>

                                    {/* Filtros */}
                                    <div className={`flex flex-wrap items-end gap-4 ${t.cardBg} p-4 rounded-2xl border ${t.cardBorder}`}>
                                        <div className={`flex-1 min-w-[200px]`}>
                                            <label className={`text-[10px] uppercase font-bold ${t.textMuted} mb-1.5 block`}>Hospital</label>
                                            <select
                                                value={filterHospitalId || ''}
                                                onChange={e => setFilterHospitalId(e.target.value || null)}
                                                className={`w-full text-sm ${t.inputBg} border ${t.cardBorder} rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-medium ${t.textWhite} shadow-sm transition-all`}
                                            >
                                                <option value="">Todos los hospitales</option>
                                                {hospitals.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
                                            </select>
                                        </div>
                                        <div className={`flex-1 min-w-[150px]`}>
                                            <label className={`text-[10px] uppercase font-bold ${t.textMuted} mb-1.5 block`}>Fecha de Ingreso (Desde)</label>
                                            <input
                                                type="date"
                                                value={dateRange.start}
                                                onChange={e => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                                                onClick={(e) => { if ('showPicker' in HTMLInputElement.prototype) { e.currentTarget.showPicker(); } }}
                                                className={`w-full text-sm ${t.inputBg} border ${t.cardBorder} rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-medium ${t.textWhite} shadow-sm transition-all cursor-pointer`}
                                            />
                                        </div>
                                        <div className={`flex-1 min-w-[150px]`}>
                                            <label className={`text-[10px] uppercase font-bold ${t.textMuted} mb-1.5 block`}>Hasta</label>
                                            <input
                                                type="date"
                                                value={dateRange.end}
                                                onChange={e => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                                                onClick={(e) => { if ('showPicker' in HTMLInputElement.prototype) { e.currentTarget.showPicker(); } }}
                                                className={`w-full text-sm ${t.inputBg} border ${t.cardBorder} rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-medium ${t.textWhite} shadow-sm transition-all cursor-pointer`}
                                            />
                                        </div>
                                        {(filterHospitalId || dateRange.start || dateRange.end) && (
                                            <button
                                                onClick={() => { setFilterHospitalId(null); setDateRange({ start: '', end: '' }); }}
                                                className={`px-5 py-2.5 text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 ${t.cardBg} border ${t.cardBorder} rounded-xl font-bold transition-all shadow-sm flex items-center gap-2`}
                                                title="Limpiar todos los filtros"
                                            >
                                                <Trash2 className={`w-4 h-4`} />
                                                Limpiar
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <div className={`overflow-x-auto`}>
                                    <table className={`w-full border-collapse`}>
                                        <thead className={`${t.tableHeader} text-left rounded-t-3xl`}>
                                            <tr>
                                                <th className={`py-5 px-6 w-12 text-center rounded-tl-2xl`}>
                                                    <input 
                                                        type="checkbox" 
                                                        className="w-4 h-4 rounded-md border-slate-400 text-indigo-500 focus:ring-indigo-500/50 cursor-pointer transition-all"
                                                        checked={filteredPatients.length > 0 && selectedPatientIds.length === filteredPatients.length}
                                                        onChange={handleSelectAll}
                                                    />
                                                </th>
                                                <th className={`py-5 px-4 w-10`}></th>
                                                <th className={`py-5 px-6 text-[10px] font-bold ${t.textMuted} uppercase tracking-widest`}>Paciente / ID</th>
                                                <th className={`py-5 px-6 text-[10px] font-bold ${t.textMuted} uppercase tracking-widest`}>Hospital</th>
                                                <th className={`py-5 px-6 text-[10px] font-bold ${t.textMuted} uppercase tracking-widest`}>Última Actividad</th>
                                                <th className={`py-5 px-6 text-[10px] font-bold ${t.textMuted} uppercase tracking-widest rounded-tr-2xl`}>Estatus Final</th>
                                            </tr>
                                        </thead>
                                        <tbody className={`divide-y divide-transparent`}>
                                            {filteredPatients.length > 0 ? (
                                                filteredPatients.map((patient, pIdx) => (
                                                    <React.Fragment key={patient.id}>
                                                        <tr
                                                            className={`transition-all duration-300 cursor-pointer border-b ${t.dividerBorder} ${expandedPatientId === patient.id ? `bg-indigo-500/5 border-transparent` : `${pIdx % 2 !== 0 ? 'bg-slate-50/50' : 'bg-white'} ${t.tableRowHover}`}`}
                                                            onClick={() => setExpandedPatientId(expandedPatientId === patient.id ? null : patient.id)}
                                                        >
                                                            <td className={`py-5 px-6 text-center`} onClick={(e) => e.stopPropagation()}>
                                                                <input 
                                                                    type="checkbox" 
                                                                    className="w-4 h-4 rounded-md border-slate-400 text-indigo-500 focus:ring-indigo-500/50 cursor-pointer transition-all"
                                                                    checked={selectedPatientIds.includes(patient.id)}
                                                                    onChange={() => togglePatientSelection(patient.id)}
                                                                />
                                                            </td>
                                                            <td className={`py-5 px-4 text-center`}>
                                                                {expandedPatientId === patient.id ? (
                                                                    <ChevronDown className={`w-4 h-4 text-indigo-400`} />
                                                                ) : (
                                                                    <ChevronRight className={`w-4 h-4 ${t.textMuted}`} />
                                                                )}
                                                            </td>
                                                            <td className={`py-5 px-6`}>
                                                                <div className={`flex items-center gap-4`}>
                                                                    <div className={`w-10 h-10 rounded-xl ${t.cardBg} border ${t.cardBorder} flex items-center justify-center shadow-sm`}>
                                                                        <span className={`font-bold text-sm ${t.textMain}`}>P</span>
                                                                    </div>
                                                                    <div>
                                                                        <div className={`font-bold ${t.textWhite} text-sm tracking-tight`}>{patient.id}</div>
                                                                        <div className={`text-xs ${t.textMuted} mt-0.5`}>{patient.procedureName || 'Sin procedimiento'}</div>
                                                                        {patient.doctorName && <div className={`text-[10px] font-medium ${t.textMuted} opacity-70 mt-0.5`}>{patient.doctorName}</div>}
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className={`py-5 px-6`}>
                                                                <div className={`text-sm font-semibold ${t.textMain}`}>
                                                                    {hospitals.find(h => h.id === patient.hospitalId)?.name || patient.hospitalId}
                                                                </div>
                                                            </td>
                                                            <td className={`py-5 px-6`}>
                                                                <div className={`text-sm font-semibold ${t.textMain}`}>
                                                                    {new Date(patient.logs[patient.logs.length - 1]?.timestamp || patient.admissionTime).toLocaleDateString()}
                                                                </div>
                                                                <div className={`text-xs ${t.textMuted} mt-0.5`}>
                                                                    {new Date(patient.logs[patient.logs.length - 1]?.timestamp || patient.admissionTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                                </div>
                                                            </td>
                                                            <td className={`py-5 px-6`}>
                                                                {(() => {
                                                                    const finalStatus = patient.currentStatus || (patient.logs && patient.logs.length > 0 ? patient.logs[patient.logs.length - 1].status : 'WAITING');
                                                                    if (finalStatus === 'EXIT') {
                                                                        return (
                                                                            <span className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold ${theme === 'dark' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'}`}>
                                                                                <CheckCircle2 className="w-4 h-4" />
                                                                                Completado
                                                                            </span>
                                                                        );
                                                                    }
                                                                    return (
                                                                        <span className={`inline-flex items-center px-3 py-2 rounded-lg text-xs font-bold ${theme === 'dark' ? 'bg-indigo-500/10 text-indigo-400 border border-indigo-500/20' : 'bg-indigo-50 text-indigo-700 border border-indigo-200'} shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]`}>
                                                                            {STATUS_CONFIG[finalStatus as keyof typeof STATUS_CONFIG]?.label || finalStatus}
                                                                        </span>
                                                                    );
                                                                })()}
                                                            </td>
                                                        </tr>
                                                        {expandedPatientId === patient.id && (
                                                            <tr>
                                                                <td colSpan={6} className={`${t.cardBg} p-0 ${t.borderBottom}`}>
                                                                    <div className={`p-6 ml-14 animate-fade-in border-l-2 border-indigo-500/30`}>
                                                                        <div className="flex items-center justify-between mb-4">
                                                                            <h4 className={`text-[10px] items-center gap-1.5 flex font-bold text-indigo-400 uppercase tracking-widest`}>
                                                                                <Activity className={`w-3 h-3`} /> Detalle de movimientos
                                                                            </h4>
                                                                            <div className="flex gap-2">
                                                                                <button
                                                                                    onClick={(e) => { e.stopPropagation(); exportBeautifulPDF(patient); }}
                                                                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-[10px] font-bold uppercase tracking-widest transition-colors ${theme === 'dark' ? 'text-indigo-400 border-indigo-500/20 hover:bg-indigo-500/10' : 'text-indigo-600 border-indigo-100 hover:bg-indigo-50'}`}
                                                                                >
                                                                                    <Download className="w-3 h-3" />
                                                                                    PDF
                                                                                </button>
                                                                                <button
                                                                                    onClick={(e) => { e.stopPropagation(); exportSinglePatient(patient); }}
                                                                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-[10px] font-bold uppercase tracking-widest transition-colors ${theme === 'dark' ? 'text-slate-400 border-slate-500/20 hover:bg-slate-500/10' : 'text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                                                                                >
                                                                                    <Download className="w-3 h-3" />
                                                                                    CSV
                                                                                </button>
                                                                                <button
                                                                                    onClick={(e) => { e.stopPropagation(); handleDeletePatient(patient.id); }}
                                                                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-[10px] font-bold uppercase tracking-widest transition-colors text-red-500 border-red-500/20 hover:bg-red-500/10 ml-2`}
                                                                                    title="Eliminar paciente"
                                                                                >
                                                                                    <Trash2 className="w-3 h-3" />
                                                                                    Eliminar
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                        <div className={`space-y-4`}>
                                                                            {(() => {
                                                                                const sortedLogs = [...(patient.logs || [])].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
                                                                                const groupedLogs: any[] = [];
                                                                                sortedLogs.forEach(log => {
                                                                                    if (groupedLogs.length > 0 && groupedLogs[groupedLogs.length - 1].status === log.status) {
                                                                                        groupedLogs[groupedLogs.length - 1].endTime = log.timestamp;
                                                                                        groupedLogs[groupedLogs.length - 1].count++;
                                                                                        groupedLogs[groupedLogs.length - 1].updatedBy = log.updatedBy || groupedLogs[groupedLogs.length - 1].updatedBy; // kept last editor
                                                                                    } else {
                                                                                        groupedLogs.push({ ...log, startTime: log.timestamp, endTime: log.timestamp, count: 1 });
                                                                                    }
                                                                                });
                                                                                
                                                                                return groupedLogs.reverse().map((log, lIdx) => {
                                                                                    const ms = new Date(log.endTime).getTime() - new Date(log.startTime).getTime();
                                                                                    const mins = Math.round(ms / 60000);
                                                                                    const showDuration = mins > 0;
                                                                                    return (
                                                                                        <div key={lIdx} className={`flex items-start gap-4`}>
                                                                                             <div className={`min-w-[80px] text-[10px] font-mono font-bold ${t.textMuted} pt-1`}>
                                                                                                {new Date(log.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                                                            </div>
                                                                                            <div className={`flex-1 ${t.cardBg} p-3 rounded-xl border ${t.cardBorder} shadow-sm flex justify-between items-center group ${t.cardHoverBg} transition-colors`}>
                                                                                                <div>
                                                                                                    <p className={`text-sm font-bold ${t.textWhite} mb-0.5`}>
                                                                                                        {STATUS_CONFIG[log.status as keyof typeof STATUS_CONFIG]?.label || log.status}
                                                                                                    </p>
                                                                                                    <p className={`text-[10px] ${t.textMuted} flex items-center gap-1`}>
                                                                                                        <Users className={`w-2.5 h-2.5`} /> Actualizado por <span className={`font-bold ${t.textMain}`}>{log.updatedBy || 'Staff'}</span>
                                                                                                    </p>
                                                                                                </div>
                                                                                                <div className={`flex items-center gap-3`}>
                                                                                                    {showDuration && (
                                                                                                        <div className={`flex flex-col items-end`}>
                                                                                                            <span className={`text-[11px] font-bold text-indigo-500 bg-indigo-50 px-2 py-0.5 rounded-md border border-indigo-100`}>{mins} min</span>
                                                                                                        </div>
                                                                                                    )}
                                                                                                    <div className={`text-[10px] ${t.textMuted} font-medium ${t.cardBg} px-2 py-1 rounded-md border ${t.cardBorder}`}>
                                                                                                        {new Date(log.startTime).toLocaleDateString()}
                                                                                                    </div>
                                                                                                </div>
                                                                                            </div>
                                                                                        </div>
                                                                                    );
                                                                                });
                                                                            })()}
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </React.Fragment>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={5} className={`py-12 text-center ${t.textMuted} text-sm`}>No hay pacientes registrados con los filtros seleccionados.</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {activeTab === 'screens' && (
                            <div className={`animate-fade-in`}>
                                {/* Screen Stats Header */}
                                <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 mb-6`}>
                                    <div className={`${t.cardBg} backdrop-blur-xl p-5 rounded-2xl border ${t.cardBorder} ${t.cardShadow} flex items-center gap-4`}>
                                        <div className={`p-3 bg-emerald-500/10 text-emerald-400 rounded-xl border border-emerald-500/30`}>
                                            <Monitor className="w-6 h-6" />
                                        </div>
                                        <div>
                                            <p className={`text-[10px] font-bold ${t.textMuted} uppercase tracking-widest`}>Online</p>
                                            <p className={`text-2xl font-black ${t.textWhite} font-mono`}>
                                                {screens.filter(s => s.isOnline).length}
                                                <span className={`text-sm ${t.textMuted} font-medium ml-1`}>/ {screens.length}</span>
                                            </p>
                                        </div>
                                    </div>
                                    <div className={`${t.cardBg} backdrop-blur-xl p-5 rounded-2xl border ${t.cardBorder} ${t.cardShadow} flex items-center gap-4`}>
                                        <div className={`p-3 bg-red-500/10 text-red-400 rounded-xl border border-red-500/30`}>
                                            <Power className="w-6 h-6" />
                                        </div>
                                        <div>
                                            <p className={`text-[10px] font-bold ${t.textMuted} uppercase tracking-widest`}>Offline</p>
                                            <p className={`text-2xl font-black ${t.textWhite} font-mono`}>
                                                {screens.filter(s => !s.isOnline).length}
                                            </p>
                                        </div>
                                    </div>
                                    <div className={`${t.cardBg} backdrop-blur-xl p-5 rounded-2xl border ${t.cardBorder} ${t.cardShadow} flex items-center gap-4`}>
                                        <div className={`p-3 bg-blue-500/10 text-blue-400 rounded-xl border border-blue-500/30`}>
                                            <LayoutGrid className="w-6 h-6" />
                                        </div>
                                        <div>
                                            <p className={`text-[10px] font-bold ${t.textMuted} uppercase tracking-widest`}>Hospitales con Pantallas</p>
                                            <p className={`text-2xl font-black ${t.textWhite} font-mono`}>
                                                {new Set(screens.map(s => s.hospitalId)).size}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Filters */}
                                <div className={`flex flex-wrap items-center gap-3 mb-6`}>
                                    <div className={`flex ${t.cardBg} p-1 rounded-xl border ${t.cardBorder}`}>
                                        {(['all', 'online', 'offline'] as const).map(f => (
                                            <button
                                                key={f}
                                                onClick={() => setScreenFilter(f)}
                                                className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${screenFilter === f
                                                    ? f === 'online' ? 'bg-emerald-600 text-white' : f === 'offline' ? 'bg-red-600 text-white' : 'bg-indigo-600 text-white'
                                                    : `${t.textMuted} hover:${t.textWhite}`
                                                    }`}
                                            >
                                                {f === 'all' ? 'Todas' : f === 'online' ? '🟢 Online' : '🔴 Offline'}
                                            </button>
                                        ))}
                                    </div>
                                    <select
                                        value={screenHospitalFilter || ''}
                                        onChange={e => setScreenHospitalFilter(e.target.value || null)}
                                        className={`text-sm ${t.inputBg} border ${t.cardBorder} rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-medium ${t.textWhite}`}
                                    >
                                        <option value="">Todos los hospitales</option>
                                        {hospitals.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
                                    </select>
                                    <button
                                        onClick={async () => {
                                            try {
                                                const data = await storageService.getScreenSessions();
                                                setScreens(data as ScreenSession[]);
                                            } catch { setScreens([]); }
                                        }}
                                        className={`ml-auto px-4 py-2.5 ${t.cardBg} border ${t.cardBorder} ${t.textWhite} rounded-xl text-xs font-bold ${t.cardHoverBg} transition-all flex items-center gap-2`}
                                    >
                                        <Activity className="w-3.5 h-3.5" /> Actualizar
                                    </button>
                                </div>

                                {/* Screen Grid */}
                                <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4`}>
                                    {screens
                                        .filter(s => {
                                            if (screenFilter === 'online' && !s.isOnline) return false;
                                            if (screenFilter === 'offline' && s.isOnline) return false;
                                            if (screenHospitalFilter && s.hospitalId !== screenHospitalFilter) return false;
                                            return true;
                                        })
                                        .map(screen => {
                                            const lastSeen = new Date(screen.lastHeartbeat);
                                            const minutesAgo = Math.round((Date.now() - lastSeen.getTime()) / 60000);
                                            const timeLabel = minutesAgo < 1 ? 'Ahora' : minutesAgo < 60 ? `hace ${minutesAgo}m` : minutesAgo < 1440 ? `hace ${Math.round(minutesAgo / 60)}h` : `hace ${Math.round(minutesAgo / 1440)}d`;

                                            return (
                                                <div key={screen.id} className={`${t.cardBg} backdrop-blur-lg rounded-2xl border ${screen.isOnline ? 'border-emerald-500/30 hover:border-emerald-500/50' : `${t.cardBorder} ${t.cardHoverBorder}`} ${t.cardShadow} p-5 transition-all group relative overflow-hidden`}>
                                                    {screen.isOnline && (
                                                        <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/5 blur-[60px] rounded-full pointer-events-none"></div>
                                                    )}

                                                    <div className="flex items-start justify-between mb-4 relative z-10">
                                                        <div className="flex items-center gap-3">
                                                            <div className={`relative p-2.5 rounded-xl border ${screen.isOnline ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' : `${t.cardBg} ${t.cardBorder} ${t.textMuted}`}`}>
                                                                {(() => {
                                                                    const combined = `${screen.os || ''} ${screen.browser || ''} ${screen.userAgent || ''}`.toLowerCase();
                                                                    let Icon = Monitor;
                                                                    if (combined.includes('tizen') || combined.includes('webos') || combined.match(/\btv\b/) || combined.includes('roku') || combined.includes('chromecast')) Icon = Tv;
                                                                    else if ((combined.includes('android') && combined.includes('mobile')) || combined.includes('iphone')) Icon = Smartphone;
                                                                    else if (combined.includes('ipad') || (combined.includes('android') && !combined.includes('mobile')) || combined.includes('tablet')) Icon = Tablet;
                                                                    else if (combined.includes('mac')) Icon = Laptop;
                                                                    return <Icon className="w-5 h-5" />;
                                                                })()}
                                                                <span className={`absolute -top-1 -right-1 w-3 h-3 rounded-full border-2 ${theme === 'dark' ? 'border-slate-900' : 'border-white'} ${screen.isOnline ? 'bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)]' : 'bg-red-400'}`}>
                                                                    {screen.isOnline && <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-75"></span>}
                                                                </span>
                                                            </div>
                                                            <div>
                                                                <h4 className={`font-bold ${t.textWhite} text-sm leading-tight`}>{screen.screenName}</h4>
                                                                <p className={`text-[10px] ${t.textMuted} font-medium`}>{screen.hospitalName || screen.handle}</p>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all">
                                                            <button
                                                                onClick={() => setPreviewScreen(screen)}
                                                                className={`p-1.5 rounded-lg ${t.textMuted} hover:text-indigo-400 hover:bg-indigo-400/10 transition-all`}
                                                                title="Previsualizar pantalla"
                                                            >
                                                                <Eye className="w-3.5 h-3.5" />
                                                            </button>
                                                            <button
                                                                onClick={() => {
                                                                    setEditScreenName(screen.screenName);
                                                                    setEditScreenGroup(screen.screenGroup || '');
                                                                    setEditForcedTheme(screen.forcedTheme || null);
                                                                    setEditScreenMode(screen.screenMode || 'BOARD');
                                                                    const modeData = (screen as any).screenModeData || '';
                                                                    if (screen.screenMode === 'MEDIA_URL') {
                                                                        setEditMediaUrl(modeData);
                                                                        setEditBoardLayout('LIST');
                                                                    } else {
                                                                        setEditMediaUrl('');
                                                                        setEditBoardLayout(modeData.startsWith('boardLayout:') ? modeData.replace('boardLayout:', '') : 'LIST');
                                                                    }
                                                                    setEditingScreen(screen);
                                                                }}
                                                                className={`p-1.5 rounded-lg ${t.textMuted} hover:text-indigo-400 hover:bg-indigo-400/10 transition-all`}
                                                                title="Editar pantalla"
                                                            >
                                                                <Edit className="w-3.5 h-3.5" />
                                                            </button>
                                                            <button
                                                                onClick={async (e) => {
                                                                    e.stopPropagation();
                                                                    if (window.confirm(`¿Eliminar la pantalla "${screen.screenName}"?`)) {
                                                                        await storageService.deleteScreenSession(screen.id);
                                                                        setScreens(prev => prev.filter(s => s.id !== screen.id));
                                                                    }
                                                                }}
                                                                className={`p-1.5 rounded-lg ${t.textMuted} hover:text-red-400 hover:bg-red-400/10 transition-all`}
                                                                title="Eliminar pantalla"
                                                            >
                                                                <Trash2 className="w-3.5 h-3.5" />
                                                            </button>
                                                        </div>
                                                    </div>

                                                    {/* Metadata */}
                                                    <div className={`space-y-2 text-xs relative z-10`}>
                                                        <div className={`flex justify-between ${t.textMuted}`}>
                                                            <span>Resolución</span>
                                                            <span className={`font-mono font-bold ${t.textMain}`}>{screen.resolution || '—'}</span>
                                                        </div>
                                                        <div className={`flex justify-between ${t.textMuted}`}>
                                                            <span>Tema</span>
                                                            <span className={`font-bold ${t.textMain} flex items-center gap-1`}>
                                                                {(screen.forcedTheme || screen.theme) === 'dark' ? <><Moon className="w-3 h-3" /> Oscuro</> : <><Sun className="w-3 h-3" /> Claro</>}
                                                                {screen.forcedTheme && <span className="text-[8px] bg-indigo-500/20 text-indigo-400 px-1 rounded ml-1">FORZADO</span>}
                                                            </span>
                                                        </div>
                                                        <div className={`flex justify-between ${t.textMuted}`}>
                                                            <span>Heartbeat</span>
                                                            <span className={`font-bold ${screen.isOnline ? 'text-emerald-400' : 'text-red-400'}`}>{timeLabel}</span>
                                                        </div>

                                                        {/* Device info separator */}
                                                        <div className={`border-t ${t.cardBorder} my-2 !mt-3`}></div>

                                                        {screen.os && (
                                                            <div className={`flex justify-between ${t.textMuted}`}>
                                                                <span>Sistema</span>
                                                                <span className={`font-bold ${t.textMain}`}>{screen.os}</span>
                                                            </div>
                                                        )}
                                                        {screen.browser && (
                                                            <div className={`flex justify-between ${t.textMuted}`}>
                                                                <span>Navegador</span>
                                                                <span className={`font-bold ${t.textMain}`}>{screen.browser}</span>
                                                            </div>
                                                        )}
                                                        {screen.ipAddress && (
                                                            <div className={`flex justify-between ${t.textMuted}`}>
                                                                <span>IP</span>
                                                                <span className={`font-mono font-bold ${t.textMain} text-[10px]`}>{screen.ipAddress}</span>
                                                            </div>
                                                        )}
                                                        <div className={`flex justify-between ${t.textMuted}`}>
                                                            <span>Primera conexión</span>
                                                            <span className={`font-mono ${t.textMain}`}>{new Date(screen.firstSeen).toLocaleDateString()}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                </div>

                                {screens.filter(s => {
                                    if (screenFilter === 'online' && !s.isOnline) return false;
                                    if (screenFilter === 'offline' && s.isOnline) return false;
                                    if (screenHospitalFilter && s.hospitalId !== screenHospitalFilter) return false;
                                    return true;
                                }).length === 0 && (
                                        <div className={`${t.cardBg} backdrop-blur-lg rounded-3xl border ${t.cardBorder} p-16 text-center`}>
                                            <Monitor className={`w-12 h-12 ${t.textMuted} mx-auto mb-4 opacity-30`} />
                                            <p className={`${t.textMuted} text-sm font-medium`}>
                                                {screens.length === 0
                                                    ? 'No hay pantallas registradas aún. Las pantallas se registran automáticamente al abrir el tablero público.'
                                                    : 'No hay pantallas con los filtros seleccionados.'}
                                            </p>
                                            {screens.length === 0 && (
                                                <p className={`text-xs ${t.textMuted} mt-2 opacity-60`}>
                                                    Abre <span className="font-mono text-indigo-400">/hospital-handle/board?screenName=Mi+Pantalla</span> en una TV para registrarla.
                                                </p>
                                            )}
                                        </div>
                                    )}
                            </div>
                        )}

                        {activeTab === 'staff_sessions' && (
                            <div className="animate-fade-in">
                                <StaffSessionsPanel
                                    hospitals={hospitals.map(h => ({ id: h.id, name: h.name }))}
                                    primaryColor="#6366F1"
                                    theme={theme}
                                    onUnauthorized={handleLogout}
                                />
                            </div>
                        )}

                        {activeTab === 'docs' && (
                            <div className="animate-fade-in">
                                <div className="flex flex-col gap-6 mb-8">
                                    <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
                                        <div>
                                            <h2 className={`text-xl font-bold ${t.textWhite} flex items-center gap-2`}>
                                                <FileCode className="w-6 h-6 text-indigo-400" /> Docs Tokens & External Links
                                            </h2>
                                            <p className={`text-sm ${t.textMuted} mt-1`}>Genera links únicos y revocables para compartir documentación técnica manual con integradores (ej: técnicos de TASY o Epic).</p>
                                        </div>
                                        <div className="flex flex-wrap items-center gap-3">
                                            <a
                                                href="/docs/admin_preview"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className={`${t.cardBg} ${t.cardHoverBg} border ${t.cardBorder} ${t.textWhite} font-bold py-2.5 px-5 rounded-xl flex items-center gap-2 shadow-sm transition-all active:scale-95`}
                                            >
                                                <Eye className="w-5 h-5" />
                                                Ver Documentación Directa
                                            </a>
                                            <button
                                                onClick={() => setShowNewTokenModal(true)}
                                                className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2.5 px-5 rounded-xl flex items-center gap-2 shadow-lg shadow-indigo-600/20 transition-all active:scale-95 whitespace-nowrap"
                                            >
                                                <Plus className="w-5 h-5" />
                                                Generar Nuevo Link
                                            </button>
                                        </div>
                                    </div>
                                    
                                    <div className={`p-4 rounded-xl border border-amber-500/30 bg-amber-500/10 flex gap-3 text-sm ${t.textWhite}`}>
                                        <ShieldCheck className="w-6 h-6 text-amber-400 shrink-0 mt-0.5" />
                                        <div>
                                            <p className="font-bold text-amber-400 mb-1">Nota Arquitectónica: API Keys y Webhooks (Self-Serve)</p>
                                            <p className="opacity-90 leading-relaxed">
                                                Los <b>Docs Tokens</b> generados aquí son SÓLO para dar acceso de lectura a ingenieros externos.<br/>Las credenciales de sistema <b>(API Keys Machine-to-Machine)</b> que usarán los sistemas EMR para enviar datos <b>NO se configuran desde aquí</b>. El personal de TI de cada sucursal debe entrar a su propio <i>Panel de Hospital → Integraciones</i> para autogenerar sus API Keys y registrar sus Webhooks asegurados.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {docTokens.map(token => (
                                        <div key={token.id} className={`${t.cardBg} backdrop-blur-lg rounded-2xl border ${token.isRevoked ? 'border-red-500/30 opacity-70' : `${t.cardBorder} ${t.cardHoverBorder}`} ${t.cardShadow} p-5 transition-all group relative overflow-hidden`}>
                                            <div className="flex justify-between items-start mb-4">
                                                <div>
                                                    <h3 className={`font-bold ${t.textWhite} text-lg mb-1`}>{token.label}</h3>
                                                    <p className={`text-[10px] ${t.textMuted} font-bold uppercase tracking-widest bg-white/5 border border-white/10 px-2 py-0.5 rounded-sm inline-block`}>
                                                        {token.hospitalId ? hospitals.find(h => h.id === token.hospitalId)?.name || token.hospitalId : 'GLOBAL ACCESS'}
                                                    </p>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        onClick={() => handleToggleTokenRevoke(token)}
                                                        className={`p-1.5 rounded-lg ${token.isRevoked ? 'text-emerald-400 hover:bg-emerald-400/10' : 'text-red-400 hover:bg-red-400/10'} transition-colors`}
                                                        title={token.isRevoked ? "Restaurar Acceso" : "Revocar Link"}
                                                    >
                                                        {token.isRevoked ? <RefreshCw className="w-4 h-4" /> : <ShieldCheck className="w-4 h-4" />}
                                                    </button>
                                                    <button
                                                        onClick={async () => {
                                                            if (window.confirm('¿Borrar token permanentemente?')) {
                                                                await storageService.deleteDocToken(token.id);
                                                                setDocTokens(prev => prev.filter(t => t.id !== token.id));
                                                            }
                                                        }}
                                                        className={`p-1.5 rounded-lg ${t.textMuted} hover:text-red-400 hover:bg-red-400/10 transition-colors`}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-2 mb-4">
                                                <input 
                                                    type="text" 
                                                    readOnly 
                                                    value={`${window.location.origin}/docs/${token.id}`}
                                                    className={`w-full text-xs font-mono bg-white/5 border border-white/10 rounded-md py-1.5 px-2 ${t.textMuted} outline-none ${token.isRevoked ? 'line-through opacity-50' : ''}`}
                                                />
                                                <button
                                                    onClick={() => !token.isRevoked && handleCopyTokenLink(token.id)}
                                                    disabled={!!token.isRevoked}
                                                    className={`p-1.5 rounded-md ${copiedTokenId === token.id ? 'bg-emerald-500/20 text-emerald-400' : 'bg-indigo-500/20 text-indigo-400 hover:bg-indigo-500/30'} transition-all disabled:opacity-50`}
                                                >
                                                    {copiedTokenId === token.id ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                                                </button>
                                            </div>

                                            <div className="flex justify-between items-center text-[10px] text-slate-500">
                                                <span>Creado: {new Date(token.createdAt).toLocaleDateString()}</span>
                                                {token.isRevoked ? (
                                                    <span className="text-red-400 font-bold uppercase px-2 py-0.5 rounded bg-red-500/10 border border-red-500/20">Revocado</span>
                                                ) : (
                                                    <span className="text-emerald-400 font-bold uppercase px-2 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/20">Activo</span>
                                                )}
                                            </div>
                                        </div>
                                    ))}

                                    {docTokens.length === 0 && (
                                        <div className={`col-span-full ${t.cardBg} backdrop-blur-lg rounded-3xl border ${t.cardBorder} p-16 text-center`}>
                                            <FileCode className={`w-12 h-12 ${t.textMuted} mx-auto mb-4 opacity-30`} />
                                            <p className={`${t.textMuted} text-sm font-medium`}>No hay tokens de acceso a documentación generados aún.</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </>
                )}

            {/* Modal */}
            <HospitalModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                hospital={selectedHospital}
                groups={groups}
                onSave={handleSaveHospital}
            />

            <GroupModal
                isOpen={isGroupModalOpen}
                onClose={() => setIsGroupModalOpen(false)}
                group={selectedGroup}
                onSave={handleSaveGroup}
            />

            <AccessModal
                isOpen={isAccessModalOpen}
                onClose={() => setIsAccessModalOpen(false)}
                hospital={selectedHospital}
                onHospitalUpdate={loadData}
            />

            <GroupAccessModal
                isOpen={isGroupAccessOpen}
                onClose={() => setIsGroupAccessOpen(false)}
                group={selectedGroup}
            />

            <GroupLinkModal
                isOpen={isGroupLinkOpen}
                onClose={() => setIsGroupLinkOpen(false)}
                group={selectedGroup}
                allHospitals={hospitals}
                onUpdate={loadData}
            />

            {/* Screen Edit Modal */}
            {editingScreen && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setEditingScreen(null)}>
                    <div className={`${theme === 'dark' ? 'bg-slate-900 border-white/10' : 'bg-white border-slate-200'} border rounded-3xl w-full max-w-lg shadow-2xl flex flex-col max-h-[90vh]`} onClick={e => e.stopPropagation()}>
                        <div className={`p-6 border-b ${theme === 'dark' ? 'border-white/10' : 'border-slate-200'} flex items-center justify-between shrink-0`}>
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-indigo-500/20 rounded-xl text-indigo-400 border border-indigo-500/20">
                                    <Monitor className="w-5 h-5" />
                                </div>
                                <h3 className={`text-lg font-bold ${t.textWhite}`}>Editar Pantalla</h3>
                            </div>
                            <button onClick={() => setEditingScreen(null)} className={`${t.textMuted} hover:${t.textWhite} p-1`}><X className="w-5 h-5" /></button>
                        </div>
                        <div className="p-6 space-y-5 overflow-y-auto flex-1">
                            <div 
                                className="w-full relative rounded-xl overflow-hidden border border-slate-200 dark:border-white/10 bg-black mb-5" 
                                style={{ paddingTop: '56.25%' }}
                                ref={el => {
                                    if (el && !el.dataset.observed) {
                                        el.dataset.observed = "true";
                                        const observer = new ResizeObserver(entries => {
                                            const width = entries[0].contentRect.width;
                                            const iframe = el.querySelector('iframe');
                                            if (iframe) iframe.style.transform = `scale(${width / 1280})`;
                                        });
                                        observer.observe(el);
                                    }
                                }}
                            >
                                <div className="absolute inset-0 w-full h-full overflow-hidden">
                                    <iframe
                                        src={`/${editingScreen.handle}/board?screenId=${editingScreen.id}&preview=true&themeOverride=${editForcedTheme || ''}&modeOverride=${editScreenMode}&layoutOverride=${editBoardLayout}`}
                                        className="absolute top-0 left-0 pointer-events-none border-0 max-w-none"
                                        style={{ width: '1280px', height: '720px', transformOrigin: '0 0' }}
                                        title="Screen Mini Preview"
                                    />
                                </div>
                                <div className="absolute top-2 right-2 bg-black/50 backdrop-blur-md px-2 py-1 rounded text-[10px] text-white font-mono flex items-center gap-1.5 z-10 border border-white/10 shadow-lg">
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span> PREVIEW
                                </div>
                            </div>

                            {/* Basic Info */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={`text-xs font-bold ${t.textMuted} uppercase tracking-widest mb-2 block`}>Nombre de Pantalla</label>
                                    <input
                                        type="text" value={editScreenName} onChange={e => setEditScreenName(e.target.value)}
                                        placeholder={editingScreen.screenName}
                                        className={`w-full ${t.cardBg} border ${t.cardBorder} rounded-xl px-4 py-3 ${t.textMain} text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all font-medium`}
                                    />
                                </div>
                                <div>
                                    <label className={`text-xs font-bold ${t.textMuted} uppercase tracking-widest mb-2 block`}>Grupo / Ubicación</label>
                                    <input
                                        type="text" value={editScreenGroup} onChange={e => setEditScreenGroup(e.target.value)}
                                        placeholder="Ej: PB, Cafetería, Piso 2"
                                        className={`w-full ${t.cardBg} border ${t.cardBorder} rounded-xl px-4 py-3 ${t.textMain} text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all font-medium`}
                                    />
                                </div>
                            </div>

                            {/* Forced Theme */}
                            <div>
                                <label className={`text-xs font-bold ${t.textMuted} uppercase tracking-widest mb-2 block`}>Tema Forzado</label>
                                <div className="flex gap-2">
                                    {[
                                        { value: null, label: 'Auto', icon: null },
                                        { value: 'dark', label: 'Oscuro', icon: Moon },
                                        { value: 'light', label: 'Claro', icon: Sun },
                                    ].map(opt => (
                                        <button
                                            key={opt.label}
                                            onClick={() => setEditForcedTheme(opt.value)}
                                            className={`flex-1 py-2.5 px-3 rounded-xl border text-sm font-bold transition-all flex items-center justify-center gap-1.5 ${editForcedTheme === opt.value
                                                ? 'bg-indigo-600 text-white border-indigo-500 shadow-lg shadow-indigo-600/20'
                                                : `${t.cardBg} ${t.cardBorder} ${t.textMuted} hover:${t.textWhite}`
                                                }`}
                                        >
                                            {opt.icon && <opt.icon className="w-3.5 h-3.5" />}
                                            {opt.label}
                                        </button>
                                    ))}
                                </div>
                                <p className={`text-[10px] ${t.textMuted} mt-2`}>
                                    {editForcedTheme ? 'La pantalla aplicará este tema en su próximo heartbeat.' : 'La pantalla usa el tema de su URL o configuración local.'}
                                </p>
                            </div>
                            
                            {/* Screen Mode Picker */}
                            <div>
                                <label className={`text-xs font-bold ${t.textMuted} uppercase tracking-widest mb-3 block`}>Modo de Operación</label>
                                <div className="grid grid-cols-4 gap-2">
                                    {([
                                        { id: 'BOARD', label: 'Médico', desc: 'Pacientes' },
                                        { id: 'FULL_ADS', label: 'Promos', desc: 'Slideshow' },
                                        { id: 'CLOCK', label: 'Reloj Zen', desc: 'Descanso' },
                                        { id: 'MEDIA_URL', label: 'Video', desc: 'YouTube' },
                                    ] as const).map(opt => (
                                        <button
                                            key={opt.id}
                                            onClick={() => setEditScreenMode(opt.id)}
                                            className={`flex flex-col items-center justify-center gap-1 py-3 px-1 rounded-xl border transition-all text-center ${
                                                editScreenMode === opt.id
                                                    ? 'border-indigo-500 bg-indigo-500/15 text-indigo-400'
                                                    : `${t.cardBg} ${t.cardBorder} ${t.textMuted} hover:border-indigo-500/40`
                                            }`}
                                        >
                                            <span className="text-[11px] font-black uppercase tracking-wide leading-none">{opt.label}</span>
                                            <span className="text-[9px] opacity-50 leading-none mt-1">{opt.desc}</span>
                                        </button>
                                    ))}
                                </div>
                                
                                {/* URL Input for MEDIA_URL mode */}
                                {editScreenMode === 'MEDIA_URL' && (
                                    <div className="mt-4 p-4 rounded-xl border bg-black/20 border-white/5">
                                        <label className={`text-xs font-bold ${t.textMuted} uppercase tracking-widest mb-2 block`}>URL del Video (YouTube / MP4)</label>
                                        <input
                                            type="text" value={editMediaUrl} onChange={e => setEditMediaUrl(e.target.value)}
                                            placeholder="https://youtube.com/watch?v=..."
                                            className={`w-full ${t.cardBg} border ${t.cardBorder} rounded-xl px-4 py-3 ${t.textMain} text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50`}
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Board Layout Picker (Only if BOARD mode) */}
                            {editScreenMode === 'BOARD' && (
                            <div>
                                <label className={`text-xs font-bold ${t.textMuted} uppercase tracking-widest mb-3 block`}>Layout del Tablero</label>
                                <div className="grid grid-cols-5 gap-2">
                                    {([
                                        { id: 'LIST', label: 'Lista', desc: 'Clásico' },
                                        { id: 'GRID_2COL', label: 'Grid 2×', desc: 'Cards' },
                                        { id: 'SIDEBAR_AD', label: 'Con Ad', desc: '70/30' },
                                        { id: 'FOCUS_SINGLE', label: 'Foco', desc: '1 a la vez' },
                                        { id: 'TICKER_ONLY', label: 'Ticker', desc: 'Marquesina' },
                                    ] as const).map(opt => (
                                        <button
                                            key={opt.id}
                                            onClick={() => setEditBoardLayout(opt.id)}
                                            className={`flex flex-col items-center gap-1 py-3 px-1 rounded-xl border transition-all text-center ${
                                                editBoardLayout === opt.id
                                                    ? 'border-indigo-500 bg-indigo-500/15 text-indigo-400'
                                                    : `${t.cardBg} ${t.cardBorder} ${t.textMuted} hover:border-indigo-500/40`
                                            }`}
                                        >
                                            <span className="text-[10px] font-black uppercase tracking-wide leading-none">{opt.label}</span>
                                            <span className="text-[8px] opacity-50 leading-none mt-0.5">{opt.desc}</span>
                                        </button>
                                    ))}
                                </div>
                                <p className={`text-[10px] ${t.textMuted} mt-2`}>
                                    {editBoardLayout === 'LIST' && 'Lista de filas clásica — alta densidad de información.'}
                                    {editBoardLayout === 'GRID_2COL' && 'Cards premium en cuadrícula — visual e impactante.'}
                                    {editBoardLayout === 'SIDEBAR_AD' && 'Pacientes a la izquierda (70%) + panel de anuncio siempre visible (30%).'}
                                    {editBoardLayout === 'FOCUS_SINGLE' && 'Un paciente ocupa toda la pantalla — rotación automática cada 8 segundos.'}
                                    {editBoardLayout === 'TICKER_ONLY' && 'Pantalla con branding del hospital + ticker de estatus en la parte inferior.'}
                                </p>
                            </div>
                            )}
                            
                            {/* Device info (read-only) */}
                            <div className={`${t.cardBg} border ${t.cardBorder} rounded-xl p-4 space-y-2`}>
                                <p className={`text-[10px] font-bold ${t.textMuted} uppercase tracking-widest mb-2`}>Información del Dispositivo</p>
                                <div className="grid grid-cols-2 gap-2 text-xs">
                                    <div className={t.textMuted}>Sistema</div>
                                    <div className={`font-bold ${t.textMain} text-right`}>{editingScreen.os || '—'}</div>
                                    <div className={t.textMuted}>Navegador</div>
                                    <div className={`font-bold ${t.textMain} text-right`}>{editingScreen.browser || '—'}</div>
                                    <div className={t.textMuted}>IP</div>
                                    <div className={`font-mono font-bold ${t.textMain} text-right text-[10px]`}>{editingScreen.ipAddress || '—'}</div>
                                    <div className={t.textMuted}>Resolución</div>
                                    <div className={`font-mono font-bold ${t.textMain} text-right`}>{editingScreen.resolution || '—'}</div>
                                </div>
                            </div>
                        </div>
                        <div className={`p-6 pt-0 flex gap-3`}>
                            <button onClick={() => setEditingScreen(null)} className={`flex-1 py-3 ${t.cardBg} border ${t.cardBorder} ${t.textMuted} rounded-xl font-bold text-sm`}>
                                Cancelar
                            </button>
                            <button
                                onClick={async () => {
                                    if (!editScreenName.trim()) return;
                                    let finalScreenModeData: string | null = null;
                                    if (editScreenMode === 'BOARD' && editBoardLayout !== 'LIST') {
                                        finalScreenModeData = `boardLayout:${editBoardLayout}`;
                                    } else if (editScreenMode === 'MEDIA_URL') {
                                        finalScreenModeData = editMediaUrl.trim() || null;
                                    }

                                    const ok = await storageService.updateScreenSession(editingScreen.id, {
                                        screenName: editScreenName.trim(),
                                        screenGroup: editScreenGroup.trim() || null,
                                        forcedTheme: editForcedTheme,
                                        screenMode: editScreenMode as any,
                                        screenModeData: finalScreenModeData,
                                    });
                                    if (ok) {
                                        setScreens(prev => prev.map(s => s.id === editingScreen.id
                                            ? { ...s, screenName: editScreenName.trim(), screenGroup: editScreenGroup.trim() || null, forcedTheme: editForcedTheme, screenMode: editScreenMode as any, screenModeData: finalScreenModeData, isRegistered: true }
                                            : s
                                        ));
                                        setEditingScreen(null);
                                    }
                                }}
                                className="flex-1 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold text-sm transition-all shadow-lg shadow-indigo-600/20"
                            >
                                Guardar Cambios
                            </button>
                        </div>
                    </div>
                </div>
            )}



            {activeTab === 'metrics' && (
                <div className={`animate-fade-in`}>
                   <CloudflareMetrics theme={theme} />
                </div>
            )}

            {activeTab === 'bugs' && (
                <div className={`animate-fade-in`}>
                    <BugReportsPanel hospitals={hospitals} t={t} />
                </div>
            )}

            {/* New Docs Token Modal */}
            {showNewTokenModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 min-h-screen" onClick={() => setShowNewTokenModal(false)}>
                    <div className={`${theme === 'dark' ? 'bg-slate-900 border-white/10' : 'bg-white border-slate-200'} border rounded-3xl w-full max-w-md shadow-2xl overflow-hidden animate-fade-in`} onClick={e => e.stopPropagation()}>
                        <div className={`p-6 border-b ${theme === 'dark' ? 'border-white/10' : 'border-slate-200'} flex items-center justify-between bg-white/5`}>
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-indigo-500/20 rounded-xl text-indigo-400 border border-indigo-500/20">
                                    <FileCode className="w-5 h-5" />
                                </div>
                                <h3 className={`text-lg font-bold ${t.textWhite}`}>Nuevo Acceso a Docs</h3>
                            </div>
                            <button onClick={() => setShowNewTokenModal(false)} className={`${t.textMuted} hover:${t.textWhite} p-1`}><X className="w-5 h-5" /></button>
                        </div>
                        <div className="p-6 space-y-5">
                            <div>
                                <label className={`text-xs font-bold ${t.textMuted} uppercase tracking-widest mb-2 block`}>Nombre del Integrador / Empresa</label>
                                <input
                                    type="text"
                                    value={newTokenLabel}
                                    onChange={e => setNewTokenLabel(e.target.value)}
                                    placeholder="Ej: Integración TASI Philips"
                                    className={`w-full ${t.inputBg} border ${t.cardBorder} rounded-xl px-4 py-3 ${t.textWhite} text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                                />
                            </div>
                            <div>
                                <label className={`text-xs font-bold ${t.textMuted} uppercase tracking-widest mb-2 block`}>Restringir a un Hospital (Opcional)</label>
                                <select
                                    value={newTokenHospitalId}
                                    onChange={e => setNewTokenHospitalId(e.target.value)}
                                    className={`w-full ${t.inputBg} border ${t.cardBorder} rounded-xl px-4 py-3 ${t.textWhite} text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                                >
                                    <option value="">Acceso Global (Todos)</option>
                                    {hospitals.map(h => (
                                        <option key={h.id} value={h.id}>{h.name}</option>
                                    ))}
                                </select>
                                <p className={`text-[10px] ${t.textMuted} mt-2 font-medium`}>Si seleccionas un hospital, el token quedará ligado visualmente a ese ID en el panel.</p>
                            </div>
                        </div>
                        <div className={`p-6 pt-0 flex gap-3`}>
                            <button onClick={() => setShowNewTokenModal(false)} className={`flex-1 py-3 ${t.cardBg} border ${t.cardBorder} ${t.textMuted} rounded-xl font-bold text-sm`}>
                                Cancelar
                            </button>
                            <button
                                onClick={handleCreateToken}
                                disabled={creatingToken || !newTokenLabel.trim()}
                                className="flex-1 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold text-sm transition-all shadow-lg shadow-indigo-600/20 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {creatingToken ? 'Generando...' : 'Generar Link Único'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            {/* ─── Preview Screen Modal ───────────────────────── */}
            {previewScreen && (
                <div className="fixed inset-0 bg-slate-900/90 backdrop-blur-md z-[100] flex flex-col items-center justify-center p-4 sm:p-8 animate-fade-in">
                    <div className="w-full max-w-6xl flex justify-between items-center mb-4">
                        <div>
                            <h3 className="text-white font-bold text-lg flex items-center gap-2">
                                <Monitor className="w-5 h-5 text-indigo-400" />
                                {previewScreen.screenName}
                            </h3>
                            <p className="text-white/50 text-sm">Transmisión en vivo de la pantalla ({previewScreen.resolution || 'Auto'})</p>
                        </div>
                        <button onClick={() => setPreviewScreen(null)} className="bg-white/10 hover:bg-white/20 text-white rounded-full p-2 transition-all">
                            <X className="w-6 h-6" />
                        </button>
                    </div>
                    <div className="w-full max-w-6xl aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl relative border border-white/10">
                        <iframe
                            src={`/${previewScreen.handle}/board?screenId=${previewScreen.id}`}
                            className="w-full h-full border-0 pointer-events-none"
                            title="Screen Preview"
                        />
                    </div>
                    <div className="mt-4 flex gap-4">
                        <p className="text-white/40 text-xs font-mono uppercase tracking-widest flex items-center justify-center gap-1">
                            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" /> LIVE STREAM
                        </p>
                    </div>
                </div>
            )}

            </div>
            </main>
        </div>
    );
};
// --- Types ---Icon
const BuildingIcon = ({ className }: { className?: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <rect x="4" y="2" width="16" height="20" rx="2" ry="2"></rect>
        <path d="M9 22v-4h6v4"></path>
        <path d="M8 6h.01"></path>
        <path d="M16 6h.01"></path>
        <path d="M12 6h.01"></path>
        <path d="M12 10h.01"></path>
        <path d="M12 14h.01"></path>
        <path d="M16 10h.01"></path>
        <path d="M16 14h.01"></path>
        <path d="M8 10h.01"></path>
        <path d="M8 14h.01"></path>
    </svg>
);

export default AdminDashboard;
